import React, { useState, useEffect, useRef, useContext, useCallback, useDeferredValue, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Pin, PinOff } from 'lucide-react';
import { PlusIcon, TrashIcon, PencilIcon, XMarkIcon, ChevronDownIcon, ChevronRightIcon, MagnifyingGlassIcon, SwatchIcon, FolderIcon } from '@heroicons/react/24/solid';
import { ArrowUturnLeftIcon, ArrowsPointingInIcon, ArrowsPointingOutIcon, BookOpenIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon, ShareIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { UserContext } from '../App';
import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import ZoomableImageModal from "../components/ZoomableImageModal";
import useMediaQuery from "../utils/useMediaQuery";
import WikiLinkAutocomplete from "../components/WikiLinkAutocomplete";
import SelectionToolbar from "../components/notebook/SelectionToolbar";
import { getTextareaSelectionRect } from "../components/notebook/textareaSelectionRect";
import SaveStatusIndicator from "../components/notebook/SaveStatusIndicator";
import StickyImagesPanel from "../components/notebook/StickyImagesPanel";
import {
  findBacklinks,
  getPastedImageFiles,
  getImageFiles,
  getStickyTargetRange,
  isFileDrag,
  movePageInSections,
  pushRecentPage,
  resolvePinnedPages,
  resolveRecentPages,
  togglePinnedPage,
} from "../components/notebook/notebookPageHelpers";
import { parseWikiLinks, findPageByName, getAllPages } from "../utils/wikiLinkParser";
import * as notebookSync from "../utils/notebookSync";
import { buildSectionsFromMetaAndPages, fillPlaceholderPages, isPlaceholderPage, removeSectionReparentingChildren } from "../utils/notebookModel";
import { useConfirm, useToast } from "../components/ui";
import {
  applyNotebookInlineFormat,
  clearNotebookInlineFormatting,
  decodeNotebookEditorText,
  encodeNotebookEditorText,
  stripNotebookInlineMarkers,
} from "../utils/notebookInlineFormatting";
import {
  addObservationBlock,
  appendObservationEmbed,
  createObservationEmbed,
  createObservationId,
  findObservationSource,
  parseObservationEmbeds,
  removeObservationEmbed,
  stripObservationBlockMarkers,
  stripObservationEmbeds,
} from "../utils/notebookObservationLinks";

// UI preferences stored in localStorage (notebook data is in Firestore only)
function getSectionKey(user) { return `notebookSelectedSection-${user || 'default'}`; }
function getPageKey(user) { return `notebookSelectedPage-${user || 'default'}`; }
function getRecentKey(user) { return `notebookRecent-${user || 'default'}`; }
function getSidebarExpandedKey(user) { return `notebookSidebarExpanded-${user || 'default'}`; }
function getRecentCollapsedKey(user) { return `notebookRecentCollapsed-${user || 'default'}`; }
function getPinnedKey(user) { return `notebookPinned-${user || 'default'}`; }
function getFocusModeKey(user) { return `notebookFocusMode-${user || 'default'}`; }

// Color palette for sections
const SECTION_COLORS = [
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Green', value: '#10b981' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Gray', value: '#6b7280' },
];

// Backward-compatible updatedAt parsing:
// - number (ms)
// - ISO string
// - Firestore Timestamp (toMillis / seconds+nanoseconds)
// eslint-disable-next-line no-unused-vars
function coerceUpdatedAtToMs(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return parsed;
    const asNum = Number(value);
    if (Number.isFinite(asNum)) return asNum;
  }
  if (value && typeof value === 'object') {
    if (typeof value.toMillis === 'function') {
      const t = value.toMillis();
      if (typeof t === 'number' && Number.isFinite(t)) return t;
    }
    if (typeof value.seconds === 'number') {
      const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : 0;
      const t = value.seconds * 1000 + Math.floor(nanos / 1e6);
      if (Number.isFinite(t)) return t;
    }
  }
  return 0;
}

const NOTEBOOK_TEXT_CLASS = "text-[#111827] dark:text-[#F9FAFB]";
const NOTEBOOK_LINK_OK_CLASS = "text-[#3B82F6] hover:text-[#2563EB] underline underline-offset-2 decoration-[#3B82F6]/70 cursor-pointer";
const NOTEBOOK_LINK_BROKEN_CLASS = "text-[#EF4444] line-through opacity-80 cursor-not-allowed";
// Bold is painted with a stroke instead of font-weight: a heavier weight has wider
// glyphs, which would make the overlay wrap differently from the textarea beneath.
const NOTEBOOK_BOLD_CLASS = "[-webkit-text-stroke:0.04em_currentColor]";
// Identical text metrics for the editor textarea and the formatted overlay above it,
// so both wrap at the same characters at every width (selection/caret stay aligned).
const NOTEBOOK_EDITOR_TEXT_CLASS = "box-border border p-4 font-sans text-base font-normal not-italic leading-relaxed tracking-normal [word-spacing:normal] whitespace-pre-wrap break-words [word-break:normal] [tab-size:4] [text-rendering:auto] [font-variant-ligatures:normal] overflow-x-hidden overflow-y-auto [scrollbar-gutter:stable]";
function renderWikiAwareText(text, sections, keyRef, extraClassName = "", onLinkClick) {
  if (!text) return [];

  const links = parseWikiLinks(text);
  if (links.length === 0) {
    return [
      <span key={`t-${keyRef.current++}`} className={`${NOTEBOOK_TEXT_CLASS} ${extraClassName}`.trim()}>
        {text}
      </span>
    ];
  }

  const nodes = [];
  let lastIndex = 0;
  links.forEach((link) => {
    if (link.startIndex > lastIndex) {
      nodes.push(
        <span key={`t-${keyRef.current++}`} className={`${NOTEBOOK_TEXT_CLASS} ${extraClassName}`.trim()}>
          {text.slice(lastIndex, link.startIndex)}
        </span>
      );
    }

    const pageRef = findPageByName(link.linkText, sections);
    if (pageRef && onLinkClick) {
      // A real inline span (not a <button>, which is inline-block and cannot wrap)
      // so long links break across lines exactly like the textarea text beneath.
      nodes.push(
        <span
          role="link"
          tabIndex={-1}
          key={`l-${keyRef.current++}`}
          className={`${extraClassName} ${NOTEBOOK_LINK_OK_CLASS}`.trim()}
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {
            event.stopPropagation();
            onLinkClick(pageRef);
          }}
        >
          {link.match}
        </span>
      );
    } else {
      nodes.push(
        <span
          key={`l-${keyRef.current++}`}
          className={`${extraClassName} ${pageRef ? NOTEBOOK_LINK_OK_CLASS : NOTEBOOK_LINK_BROKEN_CLASS}`.trim()}
        >
          {link.match}
        </span>
      );
    }
    lastIndex = link.endIndex;
  });

  if (lastIndex < text.length) {
    nodes.push(
      <span key={`t-${keyRef.current++}`} className={`${NOTEBOOK_TEXT_CLASS} ${extraClassName}`.trim()}>
        {text.slice(lastIndex)}
      </span>
    );
  }

  return nodes;
}

function renderFormattedNotebookText(text, sections, onLinkClick, keyRef) {
  const output = [];
  const inlineFormatRegex = /\*\*([^*]+)\*\*|==([\s\S]+?)==|!!([\s\S]+?)!!|\^\^([\s\S]+?)\^\^/g;

  let lastIndex = 0;
  let match;

  while ((match = inlineFormatRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      output.push(...renderWikiAwareText(text.slice(lastIndex, match.index), sections, keyRef, "", onLinkClick));
    }

    if (match[1] !== undefined) {
      output.push(...renderWikiAwareText(stripNotebookInlineMarkers(match[1]), sections, keyRef, NOTEBOOK_BOLD_CLASS, onLinkClick));
    } else if (match[2] !== undefined) {
      output.push(
        ...renderWikiAwareText(
          stripNotebookInlineMarkers(match[2]),
          sections,
          keyRef,
          "bg-yellow-200 dark:bg-yellow-500/40 text-[#111827] dark:text-[#F9FAFB] rounded-sm box-decoration-clone",
          onLinkClick
        )
      );
    } else if (match[3] !== undefined) {
      output.push(
        ...renderWikiAwareText(
          stripNotebookInlineMarkers(match[3]),
          sections,
          keyRef,
          "bg-red-200 dark:bg-red-500/35 text-[#111827] dark:text-[#F9FAFB] rounded-sm box-decoration-clone",
          onLinkClick
        )
      );
    } else if (match[4] !== undefined) {
      output.push(
        ...renderWikiAwareText(
          stripNotebookInlineMarkers(match[4]),
          sections,
          keyRef,
          "bg-blue-200 dark:bg-blue-500/35 text-[#111827] dark:text-[#F9FAFB] rounded-sm box-decoration-clone",
          onLinkClick
        )
      );
    }

    lastIndex = inlineFormatRegex.lastIndex;
  }

  if (lastIndex < text.length) {
    output.push(...renderWikiAwareText(text.slice(lastIndex), sections, keyRef, "", onLinkClick));
  }

  return output;
}

function renderNotebookPreviewText(text, sections, onLinkClick, onRemoveObservation) {
  const keyRef = { current: 0 };
  const output = [];
  const embeds = parseObservationEmbeds(text);
  let lastIndex = 0;

  embeds.forEach((embed) => {
    if (embed.startIndex > lastIndex) {
      const before = stripObservationBlockMarkers(text.slice(lastIndex, embed.startIndex));
      output.push(...renderFormattedNotebookText(before, sections, onLinkClick, keyRef));
    }

    const source = findObservationSource(sections, embed.sourcePageId, embed.blockId);
    if (source.found) {
      output.push(
        <span
          key={`observation-${keyRef.current++}`}
          className="theme-panel-alt theme-border my-3 block rounded-xl border border-l-4 p-3 shadow-subtle"
          style={{ borderLeftColor: 'var(--theme-accent)' }}
        >
          <span className="block whitespace-pre-wrap text-sm leading-6 text-[#111827] dark:text-[#F9FAFB]">
            {renderFormattedNotebookText(
              stripObservationBlockMarkers(source.text),
              sections,
              onLinkClick,
              keyRef
            )}
          </span>
          <span className="mt-2 flex items-center justify-between gap-3">
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs font-semibold theme-accent hover:underline"
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.stopPropagation();
                onLinkClick?.(source);
              }}
            >
              From {source.pageName} <span aria-hidden="true">&rarr;</span>
            </button>
            <button
              type="button"
              className="text-[11px] font-medium text-[#6B7280] hover:text-[#EF4444] dark:text-[#9CA3AF] dark:hover:text-[#FCA5A5]"
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.stopPropagation();
                onRemoveObservation?.(embed.match);
              }}
            >
              Remove
            </button>
          </span>
        </span>
      );
    } else {
      output.push(
        <span
          key={`observation-missing-${keyRef.current++}`}
          className="my-3 block rounded-xl border border-dashed border-[#FCA5A5] bg-[#FEF2F2] p-3 text-sm text-[#991B1B] dark:border-[#7F1D1D] dark:bg-[#450A0A]/30 dark:text-[#FCA5A5]"
        >
          Original observation removed
          {source.pageName && onLinkClick ? (
            <button
              type="button"
              className="ml-2 text-xs font-semibold underline"
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.stopPropagation();
                onLinkClick(source);
              }}
            >
              Open {source.pageName}
            </button>
          ) : null}
          <button
            type="button"
            className="ml-2 text-xs font-semibold underline"
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.stopPropagation();
              onRemoveObservation?.(embed.match);
            }}
          >
            Remove link
          </button>
        </span>
      );
    }
    lastIndex = embed.endIndex;
  });

  if (lastIndex < text.length) {
    const after = stripObservationBlockMarkers(text.slice(lastIndex));
    output.push(...renderFormattedNotebookText(after, sections, onLinkClick, keyRef));
  }

  // Keep caret line height stable at end of text.
  output.push(<span key={`e-${keyRef.current++}`}>{"\n"}</span>);
  return output;
}

// Note: Save logic has been moved to notebookSync.js for rock-solid WAL-based persistence

export default function NotebookPage() {
  const { currentUser } = useContext(UserContext);
  const isMobile = useMediaQuery("(max-width: 767px)");
  
  // Add loading state to prevent flash
  const [isLoading, setIsLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editingPage, setEditingPage] = useState(null);
  const editorRef = useRef();
  const highlightLayerRef = useRef(null);
  
  // New feature states
  const [searchQuery, setSearchQuery] = useState('');
  const [recentPages, setRecentPages] = useState([]);
  const [recentCollapsed, setRecentCollapsed] = useState(false);
  const [pinnedPageIds, setPinnedPageIds] = useState([]);
  const [focusMode, setFocusMode] = useState(false);
  const [stickyNotesCollapsed, setStickyNotesCollapsed] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(null);
  const [pageColorPickerOpen, setPageColorPickerOpen] = useState(null);
  const [draggedPage, setDraggedPage] = useState(null);
  const [dragOverPageIndex, setDragOverPageIndex] = useState(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const sidebarLayoutExpanded = !isMobile && sidebarExpanded;
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [draggedSection, setDraggedSection] = useState(null);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  
  // Wiki linking states
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [autocompleteQuery, setAutocompleteQuery] = useState('');
  const [autocompletePosition, setAutocompletePosition] = useState({ top: 0, left: 0 });
  const [linkStartIndex, setLinkStartIndex] = useState(-1);
  const [showLinkHelp, setShowLinkHelp] = useState(false);
  const [observationLinkPicker, setObservationLinkPicker] = useState(null);
  const [observationLinkQuery, setObservationLinkQuery] = useState('');
  const [observationLinkError, setObservationLinkError] = useState('');
  
  // Sticky image states
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectionPosition, setSelectionPosition] = useState(null);
  const [canUndo, setCanUndo] = useState(false);
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const fileInputRef = useRef();
  // Section/page/text a sticky image upload belongs to, captured when the picker opens.
  const pendingStickyTargetRef = useRef(null);
  const selectionToolbarTimeoutRef = useRef(null);
  const confirm = useConfirm();
  const toast = useToast();

  // Track if initial load is complete (prevents race conditions)
  const isInitialLoadComplete = useRef(false);
  const isHydratingRef = useRef(false);
  const retryTimeoutRef = useRef(null);
  const hasLocalEditsRef = useRef(false);
  // Section arrays that came from the cloud or the local cache, not from a local edit.
  // The save effect never writes these back: its effect can run after a newer
  // snapshot has already moved prevSectionsRef on, and diffing would then turn an
  // older (even empty, not-yet-loaded) snapshot into a phantom "edit" that
  // overwrites the cloud section list.
  const loadedSectionsRef = useRef(new WeakSet());
  const markLoadedSections = (list) => {
    if (list && typeof list === 'object') loadedSectionsRef.current.add(list);
    return list;
  };

  const [syncStatus, setSyncStatus] = useState('saved'); // saved | queued | saving | offline | error | conflict
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [hasPendingUnsaved, setHasPendingUnsaved] = useState(false);
  const [legacyWalCount, setLegacyWalCount] = useState(0);
  const [manualSaveInFlight, setManualSaveInFlight] = useState(false);
  const [localUpdatedAt] = useState(null);
  const [remoteUpdatedAt] = useState(null);
  const [conflictNotice, setConflictNotice] = useState(null); // { title, message, showReload?, showOverwrite? } | null
  const sectionsRef = useRef(sections);
  const localUpdatedAtRef = useRef(null);
  const remoteUpdatedAtRef = useRef(null);
  const lastRemoteSectionsRef = useRef([]);
  const editingSectionRef = useRef(null);
  const editingPageRef = useRef(null);
  const pageTitleFocusedRef = useRef(false);
  const manualPageTitleEditsRef = useRef(new Set());
  const undoStackRef = useRef([]);

  const navigate = useNavigate();

  // Load notebook data using new WAL-based sync system
  useEffect(() => {
    if (!currentUser?.uid) {
      setIsLoading(false);
      return;
    }
    
    const uid = currentUser.uid;
    setIsLoading(true);
    setHasLoadError(false);
    setLegacyWalCount(0);
    isInitialLoadComplete.current = false;
    isHydratingRef.current = true;
    
    // Set up sync status callback
    notebookSync.setCurrentUser(uid);
    notebookSync.setStatusCallback((status, detail) => {
      if (status === 'saving') {
        hasLocalEditsRef.current = true;
        setSyncStatus('saving');
        setHasPendingUnsaved(true);
      } else if (status === 'queued') {
        hasLocalEditsRef.current = true;
        setSyncStatus(navigator.onLine ? 'queued' : 'offline');
        setHasPendingUnsaved(true);
      } else if (status === 'saved') {
        if (isUserEditingTitles()) {
          hasLocalEditsRef.current = true;
          setSyncStatus(navigator.onLine ? 'saving' : 'offline');
          setHasPendingUnsaved(true);
          return;
        }
        hasLocalEditsRef.current = false;
        setSyncStatus(navigator.onLine ? 'saved' : 'offline');
        setLastSavedAt(Date.now());
        setConflictNotice(null);
        setHasPendingUnsaved(false);
      } else if (status === 'error') {
        hasLocalEditsRef.current = true;
        // Only show the banner for real errors (detail.error), not for pending WAL entries
        setSyncStatus('error');
        setHasPendingUnsaved(true);
        if (detail?.error) {
          setConflictNotice({
            title: 'Save failed',
            message: 'Some changes could not be saved. They are stored locally and will retry automatically.',
            showReload: false,
            showOverwrite: false
          });
        }
      }
    });
    
    // Load UI preferences from localStorage
    const loadedSelectedSection = localStorage.getItem(getSectionKey(uid));
    const loadedSelectedPage = localStorage.getItem(getPageKey(uid));
    const loadedSidebarExpanded = localStorage.getItem(getSidebarExpandedKey(uid));
    const recentData = localStorage.getItem(getRecentKey(uid));
    
    setSelectedSection(loadedSelectedSection);
    setSelectedPage(loadedSelectedPage);
    setSidebarExpanded(loadedSidebarExpanded === 'true');
    if (recentData) {
      try { setRecentPages(JSON.parse(recentData)); } catch (e) { /* ignore */ }
    }
    setRecentCollapsed(localStorage.getItem(getRecentCollapsedKey(uid)) === 'true');
    try {
      const storedPins = JSON.parse(localStorage.getItem(getPinnedKey(uid)) || '[]');
      setPinnedPageIds(Array.isArray(storedPins) ? storedPins.map(String) : []);
    } catch (e) { setPinnedPageIds([]); }
    setFocusMode(localStorage.getItem(getFocusModeKey(uid)) === 'true');
    
    let metaUnsubscribe = null;
    let pageUnsubscribes = {};
    let currentPages = {};
    let currentMeta = null;

    const extractPageIdsFromMeta = (metaData) => {
      const pageIds = new Set();
      if (!metaData || !Array.isArray(metaData.sections)) return pageIds;
      metaData.sections.forEach((section) => {
        (section.pageIds || []).forEach((pageId) => {
          if (pageId) pageIds.add(pageId);
        });
      });
      return pageIds;
    };

    const syncPageSubscriptions = (metaData) => {
      const desiredPageIds = extractPageIdsFromMeta(metaData);

      Object.keys(pageUnsubscribes).forEach((pageId) => {
        if (!desiredPageIds.has(pageId)) {
          pageUnsubscribes[pageId]();
          delete pageUnsubscribes[pageId];
          delete currentPages[pageId];
        }
      });

      desiredPageIds.forEach((pageId) => {
        if (pageUnsubscribes[pageId]) return;

        pageUnsubscribes[pageId] = notebookSync.subscribeToPage(
          uid,
          pageId,
          (pageData, metadata) => {
            if (pageData) {
              currentPages[pageId] = pageData;
            } else {
              delete currentPages[pageId];

              // Safety: never mutate cloud meta automatically from a transient/missing page doc.
              // This avoids accidental remote deletions while editing locally.
              if (metadata && !metadata.fromCache) {
                console.warn('[NOTEBOOK] Page doc missing in Firestore; skipping auto-remove for safety:', pageId);
              }
            }

            if (currentMeta) {
              const nextSections = markLoadedSections(buildSectionsFromMetaAndPages(currentMeta, currentPages));
              lastRemoteSectionsRef.current = nextSections;
              if (!isHydratingRef.current && isInitialLoadComplete.current && !hasLocalEditsRef.current && !isUserEditingTitles()) {
                prevSectionsRef.current = nextSections;
                setSections(nextSections);
                sectionsRef.current = nextSections;
              } else if (pageData) {
                // Local edits are pending: only fill rows still waiting for their
                // document. Placeholders never carry user edits.
                setSections((current) => fillPlaceholderPages(current, currentPages));
              }
            }

            if (metadata && !metadata.fromCache && !metadata.hasPendingWrites && !hasLocalEditsRef.current) {
              setSyncStatus('saved');
            }
          },
          (err) => {
            console.error('[NOTEBOOK] Page subscription error:', err);
            setHasLoadError(true);
          }
        );
      });
    };
    
    const initializeNotebook = async () => {
      try {
        // Check if we need to migrate from legacy format
        const hasNew = await notebookSync.hasNewSchema(uid);
        if (!hasNew) {
          const result = await notebookSync.migrateFromLegacy(uid);
          if (result.migrated) {
          }
        }
        
        // Load from cache + replay WAL
        const { meta, pages, walCount, legacyWalCount: olderLocalEdits = 0 } =
          await notebookSync.loadFromCacheAndWAL(uid);
        setLegacyWalCount(olderLocalEdits);
        
        currentPages = pages;
        currentMeta = meta || null;
        syncPageSubscriptions(meta);
        
        // Build sections for UI
        const sections = markLoadedSections(buildSectionsFromMetaAndPages(meta, pages));
        isHydratingRef.current = true;
        prevSectionsRef.current = sections;
        setSections(sections);
        sectionsRef.current = sections;
        
        setTimeout(() => {
          isHydratingRef.current = false;
          isInitialLoadComplete.current = true;
        }, 0);
        
        // If there are pending WAL entries, flush them
        if (walCount > 0) {
          setSyncStatus('saving');
          notebookSync.flush().catch(console.error);
        } else {
          setSyncStatus('saved');
        }
        
        // Subscribe to meta updates from cloud
        metaUnsubscribe = notebookSync.subscribeToMeta(
          uid,
          (metaData, metadata) => {
            if (!metaData) return;
            
            currentMeta = metaData;
            setHasLoadError(false);
            syncPageSubscriptions(metaData);
            const nextSections = markLoadedSections(buildSectionsFromMetaAndPages(metaData, currentPages));
            lastRemoteSectionsRef.current = nextSections;

            // Only apply if not currently editing (to avoid overwriting local changes)
            if (!isHydratingRef.current && isInitialLoadComplete.current && !hasLocalEditsRef.current && !isUserEditingTitles()) {
              prevSectionsRef.current = nextSections;
              setSections(nextSections);
              sectionsRef.current = nextSections;
            }
            
            if (!metadata.fromCache && !metadata.hasPendingWrites && !hasLocalEditsRef.current) {
              setSyncStatus('saved');
            }
          },
          (err) => {
            console.error('[NOTEBOOK] Meta subscription error:', err);
            setHasLoadError(true);
          }
        );
        
        setIsLoading(false);
        
      } catch (error) {
        console.error('[NOTEBOOK] Initialization error:', error);
        setHasLoadError(true);
        setSyncStatus('error');
        setIsLoading(false);
      }
    };
    
    initializeNotebook();
    
    return () => {
      if (metaUnsubscribe) metaUnsubscribe();
      Object.values(pageUnsubscribes).forEach(unsub => unsub());
      notebookSync.setStatusCallback(null);
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [currentUser?.uid]);

  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  useEffect(() => {
    localUpdatedAtRef.current = localUpdatedAt;
  }, [localUpdatedAt]);

  useEffect(() => {
    remoteUpdatedAtRef.current = remoteUpdatedAt;
  }, [remoteUpdatedAt]);

  // Track previous sections for change detection
  const prevSectionsRef = useRef(null);
  
  const isUserEditingTitles = () => {
    return Boolean(editingSectionRef.current || editingPageRef.current || pageTitleFocusedRef.current);
  };

  const reconcileSyncAfterTitleEditing = useCallback(() => {
    const uid = currentUser?.uid;
    if (!uid || !isInitialLoadComplete.current) return;

    const checkPendingState = async (attempt = 0) => {
      if (editingSectionRef.current || editingPageRef.current || pageTitleFocusedRef.current) return;
      try {
        const remaining = await notebookSync.refreshPendingWalCount(uid);
        if (editingSectionRef.current || editingPageRef.current || pageTitleFocusedRef.current) return;

        if (remaining === 0 && !notebookSync.hasPendingChangesSync()) {
          hasLocalEditsRef.current = false;
          setSyncStatus(navigator.onLine ? 'saved' : 'offline');
          setLastSavedAt(Date.now());
          setConflictNotice(null);
          setHasPendingUnsaved(false);
        } else if (remaining > 0) {
          hasLocalEditsRef.current = true;
          setSyncStatus(navigator.onLine ? 'queued' : 'offline');
          setHasPendingUnsaved(true);
        } else if (attempt < 10) {
          window.setTimeout(() => checkPendingState(attempt + 1), 100);
        }
      } catch (error) {
        console.error('[NOTEBOOK] Could not reconcile save status after title editing:', error);
      }
    };

    window.setTimeout(() => checkPendingState(), 0);
  }, [currentUser?.uid]);

  const isAutoGeneratedPageTitle = (name) => {
    const trimmed = (name || '').trim();
    return !trimmed || trimmed === 'Untitled' || trimmed === 'New Page' || trimmed.startsWith('Untitled Page');
  };

  const generateSmartPageTitle = (content) => {
    const cleaned = stripObservationEmbeds(stripObservationBlockMarkers(content || ''))
      .replace(/\[\[[^\]]+\]\]/g, ' ')
      .replace(/[\r\n\t]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!cleaned) return '';

    const words = cleaned.split(' ').filter(Boolean).slice(0, 8);
    if (words.length === 0) return '';

    const candidate = words.join(' ').slice(0, 56).trim();
    return candidate.endsWith('.') || candidate.endsWith(',') ? candidate.slice(0, -1) : candidate;
  };

  const shouldAutoUpdateTitle = (currentTitle, generatedTitle, pageId) => {
    if (!generatedTitle || !pageId) return false;
    if (manualPageTitleEditsRef.current.has(pageId)) return false;

    const current = (currentTitle || '').trim();
    if (!current) return true;
    if (isAutoGeneratedPageTitle(current)) return true;

    const currentLower = current.toLowerCase();
    const generatedLower = generatedTitle.toLowerCase();
    if (generatedLower === currentLower) return false;

    // Keep refining while current title is still a short prefix (e.g. "a" -> "apple")
    if (generatedLower.startsWith(currentLower) && current.length <= 24) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    editingSectionRef.current = editingSection;
    if (!editingSection) reconcileSyncAfterTitleEditing();
  }, [editingSection, reconcileSyncAfterTitleEditing]);

  useEffect(() => {
    editingPageRef.current = editingPage;
    if (!editingPage) reconcileSyncAfterTitleEditing();
  }, [editingPage, reconcileSyncAfterTitleEditing]);
  
  // Enqueue changes to WAL when sections change (optimized save system)
  useEffect(() => {
    if (!currentUser?.uid || !isInitialLoadComplete.current) return;
    if (isHydratingRef.current) return;
    
    const prevSections = prevSectionsRef.current;
    const currentSections = sections;
    prevSectionsRef.current = currentSections;
    
    if (!prevSections) return; // First render after load
    // Cloud/cache data is not a local edit (see loadedSectionsRef).
    if (loadedSectionsRef.current.has(currentSections)) return;

    // Check if meta structure changed (sections/folders)
    const prevMeta = prevSections.map(s => ({ id: s.id, name: s.name, color: s.color, collapsed: s.collapsed, isFolder: s.isFolder, parentId: s.parentId, pageIds: s.pages.map(p => p.id) }));
    const currMeta = currentSections.map(s => ({ id: s.id, name: s.name, color: s.color, collapsed: s.collapsed, isFolder: s.isFolder, parentId: s.parentId, pageIds: s.pages.map(p => p.id) }));
    
    let detectedLocalChange = false;
    if (JSON.stringify(prevMeta) !== JSON.stringify(currMeta)) {
      detectedLocalChange = true;
      // Structure/title changes should save immediately to prevent UI resets from stale snapshots
      notebookSync.enqueue({
        type: 'updateMeta',
        data: { sections: currMeta }
      }).catch(console.error);
    }
    
    // Check for actual content changes (text/sticky notes)
    for (const sec of currentSections) {
      const prevSec = prevSections.find(s => s.id === sec.id);
      if (!prevSec) continue;
      
      for (const page of sec.pages) {
        // Placeholders have no document data: never write them, and a
        // placeholder being filled by its snapshot is not a local edit.
        if (isPlaceholderPage(page)) continue;
        const prevPage = prevSec.pages.find(p => p.id === page.id);
        if (prevPage && isPlaceholderPage(prevPage)) continue;
        if (!prevPage) {
          detectedLocalChange = true;
          // New page - immediate enqueue
          notebookSync.enqueueLatest({
            type: 'updatePage',
            pageId: page.id,
            data: {
              sectionId: sec.id,
              name: page.name,
              content: page.content || '',
              stickyNotes: page.stickyImages ?? page.stickyNotes ?? [],
              color: page.color ?? null
            }
          }).catch(console.error);
        } else {
          let hasContentChange = false;
          let hasNameChange = false;
          let hasStickyChange = false;
          let hasColorChange = false;
          
          // Content changed
          if (page.content !== prevPage.content) {
            hasContentChange = true;
          }
          
          // Name changed
          if (page.name !== prevPage.name) {
            hasNameChange = true;
          }
          
          // Sticky notes changed
          const currentStickies = JSON.stringify(page.stickyImages ?? page.stickyNotes ?? []);
          const prevStickies = JSON.stringify(prevPage.stickyImages ?? prevPage.stickyNotes ?? []);
          if (currentStickies !== prevStickies) {
            hasStickyChange = true;
          }

          if ((page.color ?? null) !== (prevPage.color ?? null)) {
            hasColorChange = true;
          }
          
          if (hasContentChange || hasNameChange || hasStickyChange || hasColorChange) {
            detectedLocalChange = true;
            // Durability boundary: every edit reaches IndexedDB immediately.
            // enqueueLatest coalesces rapid changes per user+page; only the
            // subsequent cloud flush is debounced.
            notebookSync.enqueueLatest({
              type: 'updatePage',
              pageId: page.id,
              data: {
                sectionId: sec.id,
                name: page.name,
                content: page.content || '',
                stickyNotes: page.stickyImages ?? page.stickyNotes ?? [],
                color: page.color ?? null
              }
            }).catch(console.error);
          }
        }
      }
    }

    if (detectedLocalChange) {
      hasLocalEditsRef.current = true;
      setHasPendingUnsaved(true);
      setSyncStatus((prev) => (prev === 'offline' ? prev : 'saving'));
    }
    
  }, [sections, currentUser?.uid]);

  // Close link help popover when clicking outside
  useEffect(() => {
    if (!showLinkHelp) return;
    
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-link-help]')) {
        setShowLinkHelp(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLinkHelp]);

  // Flush durable local changes when the tab becomes hidden.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        notebookSync.flushImmediate().catch(console.error);
      }
    };
    
    const handleBeforeUnload = (e) => {
      if (notebookSync.hasPendingChangesSync()) {
        notebookSync.flushImmediate().catch(console.error);
        e.preventDefault();
        e.returnValue = '';
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Save selected section/page to localStorage (without forcing flush)
  useEffect(() => {
    if (currentUser?.uid) {
      if (selectedSection) {
        localStorage.setItem(getSectionKey(currentUser.uid), selectedSection);
      }
      if (selectedPage) {
        localStorage.setItem(getPageKey(currentUser.uid), selectedPage);
      }
    }
  }, [selectedSection, selectedPage, currentUser?.uid]);

  // Track recent pages
  useEffect(() => {
    if (currentUser?.uid && selectedSection && selectedPage) {
      const section = sections.find(s => s.id === selectedSection);
      const page = section?.pages.find(p => p.id === selectedPage);
      
      if (section && page) {
        setRecentPages(prev => {
          // Already the most recent page: skip the write (this runs on every edit).
          if (prev[0]?.sectionId === selectedSection && prev[0]?.pageId === selectedPage) return prev;
          const updated = pushRecentPage(prev, {
            sectionId: selectedSection,
            pageId: selectedPage,
            sectionName: section.name,
            pageName: page.name,
            timestamp: Date.now(),
          });
          try {
            localStorage.setItem(getRecentKey(currentUser.uid), JSON.stringify(updated));
          } catch (e) { /* storage unavailable */ }
          return updated;
        });
      }
    }
  }, [selectedSection, selectedPage, currentUser?.uid, sections]);

  // Section helpers
  const addSection = () => {
    const id = Date.now().toString();
    const newSection = { id, name: "Untitled Section", pages: [], collapsed: false, color: '#3b82f6', isFolder: false, parentId: null };
    setSections(s => [...s, newSection]);
    setSelectedSection(id);
    setSelectedPage(null);
    setEditingSection(id);
    
    // Enqueue section addition
    if (currentUser?.uid) {
      notebookSync.enqueue({ 
        type: 'addSection', 
        section: { id, name: "Untitled Section", color: '#3b82f6', collapsed: false, isFolder: false, parentId: null, pageIds: [] }
      }).catch(console.error);
    }
    return id;
  };
  const addFolder = () => {
    const id = Date.now().toString();
    const newFolder = { 
      id, 
      name: "Untitled Folder", 
      pages: [], 
      collapsed: false, 
      color: '#3b82f6',
      isFolder: true,
      parentId: null
    };
    setSections(s => [...s, newFolder]);
    setEditingSection(id);
    
    // Enqueue folder addition
    if (currentUser?.uid) {
      notebookSync.enqueue({ 
        type: 'addSection', 
        section: { id, name: "Untitled Folder", color: '#3b82f6', collapsed: false, isFolder: true, parentId: null, pageIds: [] }
      }).catch(console.error);
    }
  };
  const deleteSection = async (id) => {
    const sectionToDelete = sections.find(s => s.id === id);
    if (!sectionToDelete) return;
    const childCount = sectionToDelete.isFolder ? sections.filter(s => s.parentId === id && !s.isFolder).length : 0;
    const pageCount = sectionToDelete.pages?.length || 0;
    const ok = await confirm({
      tone: 'danger',
      title: sectionToDelete.isFolder ? `Delete folder "${sectionToDelete.name}"?` : `Delete section "${sectionToDelete.name}"?`,
      description: sectionToDelete.isFolder
        ? (childCount > 0 ? `Its ${childCount === 1 ? 'section' : `${childCount} sections`} and their pages are kept and move to the top level.` : 'The folder is empty.')
        : (pageCount > 0 ? `This permanently deletes ${pageCount === 1 ? '1 page' : `${pageCount} pages`} in this section. This can't be undone.` : 'This section has no pages.'),
      confirmLabel: sectionToDelete.isFolder ? 'Delete folder' : 'Delete section',
    });
    if (!ok) return;

    // Sections inside a deleted folder move to the top level instead of disappearing.
    const { removedPageIds: pageIdsToDelete, movedSectionIds } = removeSectionReparentingChildren(sectionsRef.current || sections, id);
    setSections(s => removeSectionReparentingChildren(s, id).sections);

    if (selectedSection === id) {
      setSelectedSection(null);
      setSelectedPage(null);
    }
    
    // Enqueue section deletion + page deletions
    if (currentUser?.uid && isInitialLoadComplete.current) {
      notebookSync.enqueue({ type: 'deleteSection', sectionId: id }).catch(console.error);
      for (const pageId of pageIdsToDelete) {
        notebookSync.enqueue({ type: 'deletePage', pageId }).catch(console.error);
      }
    }
    if (movedSectionIds.length > 0) {
      toast.success(`Folder deleted. ${movedSectionIds.length === 1 ? '1 section' : `${movedSectionIds.length} sections`} moved to the top level.`);
    }
  };
  const renameSection = (id, name) => {
    setSections(s => s.map(sec => sec.id === id ? { ...sec, name } : sec));
  };
  const toggleSectionCollapse = (id) => {
    setSections(s => s.map(sec => sec.id === id ? { ...sec, collapsed: !sec.collapsed } : sec));
  };
  const changeSectionColor = (id, color) => {
    setSections(s => s.map(sec => sec.id === id ? { ...sec, color } : sec));
    setColorPickerOpen(null);
  };

  const changePageColor = (sectionId, pageId, color) => {
    setSections(s => s.map(sec =>
      sec.id === sectionId
        ? { ...sec, pages: sec.pages.map(p => p.id === pageId ? { ...p, color } : p) }
        : sec
    ));
    setPageColorPickerOpen(null);
  };

  // Page helpers
  const addPage = (sectionId) => {
    const id = Date.now().toString();
    setSections(s => {
      const next = s.map(sec =>
        sec.id === sectionId
          ? { ...sec, pages: [...sec.pages, { id, name: "Untitled Page", content: "", color: null }] }
          : sec
      );

      // Enqueue both page doc AND meta update atomically so neither can be lost
      if (currentUser?.uid) {
        notebookSync.enqueueLatest({
          type: 'updatePage', 
          pageId: id,
          data: { sectionId, name: "Untitled Page", content: "", stickyNotes: [], color: null }
        }).catch(console.error);

        const metaSections = next.map(sec => ({
          id: sec.id, name: sec.name, color: sec.color,
          collapsed: sec.collapsed, isFolder: sec.isFolder,
          parentId: sec.parentId, pageIds: sec.pages.map(p => p.id)
        }));
        notebookSync.enqueue({
          type: 'updateMeta',
          data: { sections: metaSections }
        }).catch(console.error);
      }

      return next;
    });
    setSelectedSection(sectionId);
    setSelectedPage(id);
    setEditingPage(id);
  };
  const createNotebookPage = () => {
    const firstSection = sections.find((candidate) => !candidate.isFolder);
    const sectionId = firstSection?.id || addSection();
    addPage(sectionId);
    setEditingSection(null);
  };
  const deletePage = async (sectionId, pageId) => {
    const target = sections.find(s => s.id === sectionId)?.pages?.find(p => p.id === pageId);
    const missing = isPlaceholderPage(target);
    const ok = await confirm({
      tone: 'danger',
      title: missing ? 'Remove missing page?' : `Delete page "${target?.name || 'Untitled'}"?`,
      description: missing
        ? "This page's content couldn't be found. Removing it clears the empty entry from the section."
        : "The page and its sticky notes are deleted. This can't be undone.",
      confirmLabel: missing ? 'Remove page' : 'Delete page',
    });
    if (!ok) return;
    // Update state
    setSections(s => s.map(sec =>
      sec.id === sectionId
        ? { ...sec, pages: sec.pages.filter(p => p.id !== pageId) }
        : sec
    ));
    
    if (selectedPage === pageId) setSelectedPage(null);
    
    // Enqueue page deletion
    if (currentUser?.uid && isInitialLoadComplete.current) {
      notebookSync.enqueue({ type: 'deletePage', pageId }).catch(console.error);
    }
  };
  const renamePage = (sectionId, pageId, name, options = {}) => {
    const markManual = options.markManual ?? true;
    if (markManual && pageId) {
      manualPageTitleEditsRef.current.add(pageId);
    }
    setSections(s => s.map(sec =>
      sec.id === sectionId
        ? { ...sec, pages: sec.pages.map(p => p.id === pageId ? { ...p, name } : p) }
        : sec
    ));
  };
  // State changes are durably persisted by the sections effect above.
  const setPageContent = useCallback((sectionId, pageId, content) => {
    setSections(prevSections => {
      return prevSections.map(sec =>
        sec.id === sectionId
          ? { ...sec, pages: sec.pages.map(p => p.id === pageId ? { ...p, content } : p) }
          : sec
      );
    });
  }, []);

  // Drag and drop helpers (pages). Moves go through the sections state, so the
  // save effect persists the new meta pageIds order and the page's sectionId.
  const handleDragStart = (e, sectionId, pageId) => {
    e.stopPropagation(); // Prevent event from bubbling to section drag handlers
    setDraggedPage({ sectionId, pageId });
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', ''); } catch (err) { /* Firefox needs some data */ }
  };

  // Handler for section containers that allows page drags to pass through
  const handleSectionContainerDragOver = (e) => {
    e.preventDefault(); // Always prevent default first to allow drops

    // If dragging a page, don't stop propagation - let it bubble to page handlers
    if (draggedPage) {
      return;
    }

    // Only stop propagation for section drags
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  // Clear drag state when drag ends
  const handleDragEnd = () => {
    setDraggedPage(null);
    setDraggedSection(null);
    setDragOverPageIndex(null);
  };

  const getRowDropIndex = (e, pageIndex) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return e.clientY - rect.top < rect.height / 2 ? pageIndex : pageIndex + 1;
  };

  // The whole page row is a drop target: top half inserts before it, bottom half after.
  const handlePageElementDragOver = (e, sectionId, pageIndex) => {
    if (!draggedPage) return;
    e.preventDefault();
    e.stopPropagation();
    const dropIndex = getRowDropIndex(e, pageIndex);
    if (dragOverPageIndex?.sectionId !== sectionId || dragOverPageIndex?.pageIndex !== dropIndex) {
      setDragOverPageIndex({ sectionId, pageIndex: dropIndex });
    }
    e.dataTransfer.dropEffect = 'move';
  };

  const commitPageMove = (targetSectionId, targetIndex) => {
    const moving = draggedPage;
    setDraggedPage(null);
    setDragOverPageIndex(null);
    if (!moving) return;
    setSections((current) => movePageInSections(current, moving.pageId, targetSectionId, targetIndex));
    if (String(selectedPage) === String(moving.pageId)) setSelectedSection(targetSectionId);
  };

  const handlePageElementDrop = (e, sectionId, pageIndex) => {
    if (!draggedPage) return;
    e.preventDefault();
    e.stopPropagation();
    commitPageMove(sectionId, getRowDropIndex(e, pageIndex));
  };

  // Dropping on a section (header or empty space) appends the page to it.
  const handleDrop = (e, targetSectionId) => {
    e.preventDefault();
    if (!draggedPage) return;
    if (draggedPage.sectionId === targetSectionId) {
      // Same section: only the row targets reorder; a stray drop changes nothing.
      handleDragEnd();
      return;
    }
    const target = sections.find((s) => s.id === targetSectionId);
    commitPageMove(targetSectionId, target ? target.pages.length : 0);
  };

  const handleSectionDragStart = (e, sectionId) => {
    // Don't start section drag if a page is being dragged
    if (draggedPage) return;
    
    setDraggedSection(sectionId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleSectionDragOver = (e, folderId) => {
    // Only handle section drags, ignore page drags
    if (draggedPage) return;
    
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleSectionDrop = (e, folderId) => {
    // Only handle section drags, ignore page drags
    if (draggedPage) return;
    
    e.preventDefault();
    e.stopPropagation();
    if (!draggedSection || draggedSection === folderId) {
      setDraggedSection(null);
      return;
    }
    
    // Move section into folder or out of folder
    setSections(s => s.map(sec => {
      if (sec.id === draggedSection) {
        return { ...sec, parentId: folderId || null };
      }
      return sec;
    }));
    
    setDraggedSection(null);
  };

  // Sticky image helpers - NO direct saves, just state updates
  const addStickyImage = (sectionId, pageId, imageUrl, position) => {
    setSections(prevSections => {
      return prevSections.map(sec =>
        sec.id === sectionId
          ? { 
              ...sec, 
              pages: sec.pages.map(p => 
                p.id === pageId 
                  ? { 
                      ...p, 
                      stickyImages: (() => {
                        const existingImages = p.stickyImages || [];
                        
                        // Check if a sticky note already exists for this text range
                        const existingNote = existingImages.find(note => 
                          note.textStart === position.textStart && note.textEnd === position.textEnd
                        );
                        
                        if (existingNote) {
                          // Add image to existing note
                          return existingImages.map(note => 
                            note.id === existingNote.id 
                              ? { ...note, images: [...(note.images || [note.imageUrl]), imageUrl] }
                              : note
                          );
                        } else {
                          // Create new sticky note with single image
                          return [
                            ...existingImages,
                            {
                              id: Date.now().toString(),
                              imageUrl, // Keep for backward compatibility
                              images: [imageUrl],
                              selectedText: position.selectedText,
                              textStart: position.textStart,
                              textEnd: position.textEnd,
                              createdAt: new Date().toISOString()
                            }
                          ];
                        }
                      })()
                    }
                  : p
              )
            }
          : sec
      );
    });
  };

  const confirmDeleteStickyNote = async (note) => {
    if (!note || !selectedSection || !selectedPage) return;
    const sectionId = selectedSection;
    const pageId = selectedPage;
    const count = Array.isArray(note.images) && note.images.length > 0 ? note.images.length : 1;
    const ok = await confirm({
      tone: 'danger',
      title: 'Delete sticky note?',
      description: count > 1
        ? `Its ${count} images are removed from this page.`
        : 'Its image is removed from this page.',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    deleteStickyImage(sectionId, pageId, note.id);
  };

  const deleteStickyImage = (sectionId, pageId, imageId) => {
    setSections(prevSections => {
      return prevSections.map(sec =>
        sec.id === sectionId
          ? { 
              ...sec, 
              pages: sec.pages.map(p => 
                p.id === pageId 
                  ? { 
                      ...p, 
                      stickyImages: (p.stickyImages || []).filter(img => img.id !== imageId)
                    }
                  : p
              )
            }
          : sec
      );
    });
  };

  // Mouse-based text selection handling
  const [, setMousePosition] = useState(null);
  
  const handleMouseUp = (e) => {
    if (!editorRef.current) return;
    // Escape just closed the toolbar; its keyup must not reopen it.
    if (e?.key === 'Escape') return;
    if (selectionToolbarTimeoutRef.current) {
      clearTimeout(selectionToolbarTimeoutRef.current);
      selectionToolbarTimeoutRef.current = null;
    }
    const textarea = editorRef.current;
    const textStart = textarea.selectionStart ?? 0;
    const textEnd = textarea.selectionEnd ?? textStart;
    const selectedText = decodeNotebookEditorText(
      (textarea.value || "").slice(textStart, textEnd)
    ).trim();

    if (textEnd > textStart && selectedText) {
      // Use mouse coordinates when available. Keyboard selections (Shift+Arrow,
      // Ctrl/Cmd+A) get a stable position near the top of the editor.
      const textareaRect = textarea.getBoundingClientRect();
      const hasPointerCoordinates = Number.isFinite(e?.clientX) && Number.isFinite(e?.clientY);
      const buttonX = hasPointerCoordinates
        ? e.clientX - textareaRect.left
        : Math.min(Math.max(textareaRect.width / 2, 24), textareaRect.width - 220);
      const buttonY = hasPointerCoordinates
        ? e.clientY - textareaRect.top
        : Math.min(Math.max(56, 24), textareaRect.height - 56);

      // Box around the selected text (falls back to the pointer/keyboard spot)
      // so the toolbar can sit just above it without covering it.
      const selectionRect = getTextareaSelectionRect(textarea, textStart, textEnd);
      const anchor = selectionRect
        ? {
            top: selectionRect.top + textarea.offsetTop,
            bottom: selectionRect.bottom + textarea.offsetTop,
            left: selectionRect.left + textarea.offsetLeft,
            right: selectionRect.right + textarea.offsetLeft,
          }
        : { top: buttonY - 14, bottom: buttonY + 14, left: buttonX, right: buttonX };

      const nextSelectionPosition = {
        x: buttonX,
        y: buttonY,
        anchor,
        selectedText,
        textStart,
        textEnd
      };

      // Delay the toolbar until after the browser finishes dispatching the
      // click from the drag-selection. Without this, the newly mounted toolbar
      // can land under the pointer and accidentally trigger Bold/Yellow.
      selectionToolbarTimeoutRef.current = window.setTimeout(() => {
        setSelectionPosition(nextSelectionPosition);
        setMousePosition({ x: buttonX, y: buttonY });
        selectionToolbarTimeoutRef.current = null;
      }, 100);
    } else {
      setSelectionPosition(null);
      setMousePosition(null);
    }
  };

  const runSelectionToolbarAction = (action) => {
    action();
    setSelectionPosition(null);
    setMousePosition(null);
  };

  const dismissSelectionToolbar = useCallback(() => {
    setSelectionPosition(null);
    setMousePosition(null);
  }, []);

  // Track which text ranges have images
  const [highlightedImageId, setHighlightedImageId] = useState(null);
  
  const navigateToNotebookPage = (pageRef) => {
    if (!pageRef) return;
    setSelectedSection(pageRef.sectionId);
    setSelectedPage(pageRef.pageId);
  };

  // Navigate on a direct click inside wiki-link text; otherwise handle sticky highlight.
  const handleTextClick = () => {
    if (!editorRef.current) return;

    // Read caret position on next tick so browser has set selection after click
    requestAnimationFrame(() => {
      if (!editorRef.current) return;
      const cursorPosition = editorRef.current.selectionStart;
      const content = page?.content ?? "";

      const links = parseWikiLinks(content);
      const linkAtCursor = links.find(
        (l) => cursorPosition >= l.startIndex && cursorPosition <= l.endIndex
      );
      if (linkAtCursor) {
        const ref = findPageByName(linkAtCursor.linkText, sections);
        if (ref) {
          navigateToNotebookPage(ref);
          return;
        }
      }

      if (!page?.stickyImages) {
        setHighlightedImageId(null);
        return;
      }

      const clickedImage = page.stickyImages.find((img) => {
        if (typeof img.textStart !== "number" || typeof img.textEnd !== "number") return false;
        return cursorPosition >= img.textStart && cursorPosition <= img.textEnd;
      });
      setHighlightedImageId(clickedImage ? clickedImage.id : null);
    });
  };

  // Image upload handling
  const handleImageUpload = async (files) => {
    const target = pendingStickyTargetRef.current;
    pendingStickyTargetRef.current = null;
    const fileArray = Array.from(files || []);
    if (fileArray.length === 0 || !currentUser?.uid || !target) return;

    setUploadingImage(true);
    try {
      const results = await Promise.allSettled(fileArray.map(async (file, index) => {
        // Unique per file: several pasted images often share a name like image.png.
        const uniqueId = `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`;
        const imageRef = ref(storage, `sticky-images/${currentUser.uid}/${uniqueId}-${file.name}`);
        await uploadBytes(imageRef, file);
        return getDownloadURL(imageRef);
      }));
      const imageUrls = results.filter((r) => r.status === 'fulfilled').map((r) => r.value);
      const failed = results.length - imageUrls.length;

      // All images from one pick go to the same sticky note.
      imageUrls.forEach((imageUrl) => addStickyImage(target.sectionId, target.pageId, imageUrl, target));
      if (failed > 0) {
        const reason = results.find((r) => r.status === 'rejected')?.reason;
        console.error('Error uploading images:', reason?.message || reason);
        toast.error(`${failed === 1 ? '1 image' : `${failed} images`} could not be uploaded. Check your connection and try again.`);
      }
      return imageUrls.length;
    } finally {
      setUploadingImage(false);
    }
  };

  const triggerImageUpload = () => {
    if (!selectionPosition?.selectedText || !selectedSection || !selectedPage) return;
    pendingStickyTargetRef.current = {
      sectionId: selectedSection,
      pageId: selectedPage,
      selectedText: selectionPosition.selectedText,
      textStart: selectionPosition.textStart,
      textEnd: selectionPosition.textEnd,
    };
    fileInputRef.current?.click();
  };

  // Pasted or dropped images become a sticky note: on the selected text, else on
  // the caret's line (see getStickyTargetRange), else a page-level note.
  const attachImagesAtCaret = async (files) => {
    const imageFiles = Array.from(files || []);
    if (imageFiles.length === 0 || !selectedSection || !selectedPage) return;
    const textarea = editorRef.current;
    const value = textarea?.value ?? encodeNotebookEditorText(page?.content || '');
    const { textStart, textEnd } = getStickyTargetRange(
      value,
      textarea?.selectionStart ?? value.length,
      textarea?.selectionEnd ?? value.length
    );
    pendingStickyTargetRef.current = {
      sectionId: selectedSection,
      pageId: selectedPage,
      selectedText: decodeNotebookEditorText(value.slice(textStart, textEnd)).trim(),
      textStart,
      textEnd,
    };
    setSelectionPosition(null);
    setStickyNotesCollapsed(false);
    const added = await handleImageUpload(imageFiles);
    if (added > 0) {
      toast.success(added === 1 ? 'Image added to sticky notes' : `${added} images added to sticky notes`);
    }
  };

  const handleEditorPaste = (event) => {
    const images = getPastedImageFiles(event.clipboardData);
    if (images.length === 0) return; // plain text paste stays native
    event.preventDefault();
    attachImagesAtCaret(images);
  };

  const [isImageDragOver, setIsImageDragOver] = useState(false);
  const imageDragDepthRef = useRef(0);

  const handleEditorDragEnter = (event) => {
    if (!isFileDrag(event.dataTransfer)) return;
    event.preventDefault();
    imageDragDepthRef.current += 1;
    setIsImageDragOver(true);
  };

  const handleEditorDragOver = (event) => {
    if (!isFileDrag(event.dataTransfer)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleEditorDragLeave = (event) => {
    if (!isFileDrag(event.dataTransfer)) return;
    imageDragDepthRef.current = Math.max(0, imageDragDepthRef.current - 1);
    if (imageDragDepthRef.current === 0) setIsImageDragOver(false);
  };

  const handleEditorDrop = (event) => {
    if (!isFileDrag(event.dataTransfer)) return;
    // Files dropped on the editor must never navigate the tab to the file.
    event.preventDefault();
    imageDragDepthRef.current = 0;
    setIsImageDragOver(false);
    const images = getImageFiles(event.dataTransfer);
    if (images.length === 0) {
      toast.error('Only images can be dropped onto a page.');
      return;
    }
    attachImagesAtCaret(images);
  };

  // Gallery modal state
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const openGallery = (stickyNote) => {
    const images =
      (Array.isArray(stickyNote?.images) && stickyNote.images.length > 0)
        ? stickyNote.images
        : (stickyNote?.imageUrl ? [stickyNote.imageUrl] : []);
    setGalleryImages(images);
    setCurrentImageIndex(0);
    setShowGalleryModal(true);
  };

  const addSectionToFolder = (folderId) => {
    const newSection = {
      id: Date.now().toString(),
      name: 'New Section',
      pages: [],
      collapsed: false,
      color: '#3b82f6',
      isFolder: false,
      parentId: folderId
    };
    
    setSections(s => [...s, newSection]);
    setEditingSection(newSection.id);

    if (currentUser?.uid) {
      notebookSync.enqueue({
        type: 'addSection',
        section: {
          id: newSection.id,
          name: newSection.name,
          color: newSection.color,
          collapsed: newSection.collapsed,
          isFolder: newSection.isFolder,
          parentId: newSection.parentId,
          pageIds: []
        }
      }).catch(console.error);
    }
  };

  const pushUndoSnapshot = (previousValue) => {
    const undoStack = undoStackRef.current;
    if (undoStack.length === 0 || undoStack[undoStack.length - 1] !== previousValue) {
      undoStack.push(previousValue);
      if (undoStack.length > 200) undoStack.shift();
      setCanUndo(true);
    }
  };

  const applyInlineFormat = (marker) => {
    if (!editorRef.current || !selectedSection || !selectedPage) return;

    const textarea = editorRef.current;
    const currentValue = page?.content || "";
    const selectionStart = textarea.selectionStart ?? 0;
    const selectionEnd = textarea.selectionEnd ?? selectionStart;
    const result = applyNotebookInlineFormat(
      currentValue,
      selectionStart,
      selectionEnd,
      marker
    );
    if (!result.changed) return;

    pushUndoSnapshot(currentValue);
    setPageContent(selectedSection, selectedPage, result.value);

    setTimeout(() => {
      if (!editorRef.current) return;
      editorRef.current.focus();
      editorRef.current.setSelectionRange(result.selectionStart, result.selectionEnd);
    }, 0);
  };

  const clearInlineFormatting = () => {
    if (!editorRef.current || !selectedSection || !selectedPage) return;

    const textarea = editorRef.current;
    const currentValue = page?.content || "";
    const selectionStart = textarea.selectionStart ?? 0;
    const selectionEnd = textarea.selectionEnd ?? selectionStart;
    const result = clearNotebookInlineFormatting(
      currentValue,
      selectionStart,
      selectionEnd
    );
    if (!result.changed) return;

    pushUndoSnapshot(currentValue);
    setPageContent(selectedSection, selectedPage, result.value);
    setTimeout(() => {
      if (!editorRef.current) return;
      editorRef.current.focus();
      editorRef.current.setSelectionRange(result.selectionStart, result.selectionEnd);
    }, 0);
  };

  const openObservationLinkPicker = () => {
    if (!selectionPosition?.selectedText || !selectedSection || !selectedPage) return;
    setObservationLinkPicker({
      sourceSectionId: selectedSection,
      sourcePageId: selectedPage,
      selectedText: selectionPosition.selectedText,
      textStart: selectionPosition.textStart,
      textEnd: selectionPosition.textEnd,
    });
    setObservationLinkQuery('');
    setObservationLinkError('');
    setSelectionPosition(null);
  };

  const closeObservationLinkPicker = () => {
    setObservationLinkPicker(null);
    setObservationLinkQuery('');
    setObservationLinkError('');
  };

  const linkObservationToPage = (destination) => {
    if (!observationLinkPicker || !destination) return;
    const sourceSection = sections.find((candidate) => candidate.id === observationLinkPicker.sourceSectionId);
    const sourcePage = sourceSection?.pages?.find((candidate) => candidate.id === observationLinkPicker.sourcePageId);
    if (!sourcePage) {
      setObservationLinkError('The source page is no longer available.');
      return;
    }
    if (String(destination.pageId) === String(sourcePage.id)) {
      setObservationLinkError('Choose a different page for this observation.');
      return;
    }

    const currentSelection = String(sourcePage.content || '').slice(
      observationLinkPicker.textStart,
      observationLinkPicker.textEnd
    );
    if (currentSelection.trim() !== observationLinkPicker.selectedText.trim()) {
      setObservationLinkError('That text changed. Close this window and select the observation again.');
      return;
    }

    const blockResult = addObservationBlock(
      sourcePage.content,
      observationLinkPicker.textStart,
      observationLinkPicker.textEnd,
      createObservationId()
    );
    if (!blockResult.blockId) {
      setObservationLinkError('Select one complete observation instead of overlapping an existing linked observation.');
      return;
    }

    const embed = createObservationEmbed(sourcePage.id, blockResult.blockId);
    pushUndoSnapshot(sourcePage.content || '');
    setSections((currentSections) => currentSections.map((currentSection) => ({
      ...currentSection,
      pages: (currentSection.pages || []).map((currentPage) => {
        if (String(currentPage.id) === String(sourcePage.id)) {
          return { ...currentPage, content: blockResult.content };
        }
        if (String(currentPage.id) === String(destination.pageId)) {
          return { ...currentPage, content: appendObservationEmbed(currentPage.content, embed) };
        }
        return currentPage;
      }),
    })));

    setSelectedSection(destination.sectionId);
    setSelectedPage(destination.pageId);
    setIsEditorFocused(false);
    closeObservationLinkPicker();
  };

  const removeLinkedObservation = (embed) => {
    if (!selectedSection || !selectedPage || !page) return;
    const nextContent = removeObservationEmbed(page.content, embed);
    if (nextContent === page.content) return;
    pushUndoSnapshot(page.content || '');
    setPageContent(selectedSection, selectedPage, nextContent);
  };

  // Editor input handler with optimized debouncing
  const handleEditorInput = (e) => {
    const value = decodeNotebookEditorText(e.target.value);
    const cursorPos = e.target.selectionStart;
    const currentValue = page?.content || "";
    if (currentValue !== value) pushUndoSnapshot(currentValue);
    setPageContent(selectedSection, selectedPage, value);

    if (
      selectedSection &&
      selectedPage &&
      page &&
      shouldAutoUpdateTitle(page.name, generateSmartPageTitle(value), page.id)
    ) {
      const smartTitle = generateSmartPageTitle(value);
      if (smartTitle && smartTitle !== page.name) {
        renamePage(selectedSection, selectedPage, smartTitle, { markManual: false });
      }
    }
    
    // Handle autocomplete detection (debounced separately)
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastOpenBracket = textBeforeCursor.lastIndexOf('[[');
    const lastCloseBracket = textBeforeCursor.lastIndexOf(']]');
    
    if (lastOpenBracket !== -1 && (lastCloseBracket === -1 || lastCloseBracket < lastOpenBracket)) {
      // We're inside a [[...]] pattern
      const query = textBeforeCursor.substring(lastOpenBracket + 2);
      setAutocompleteQuery(query);
      setLinkStartIndex(lastOpenBracket);
      setAutocompleteOpen(true);
      
      // Calculate dropdown position relative to cursor
      if (editorRef.current) {
        const textarea = editorRef.current;
        const style = window.getComputedStyle(textarea);
        const paddingTop = parseInt(style.paddingTop) || 0;
        const paddingLeft = parseInt(style.paddingLeft) || 0;
        const lineHeight = parseInt(style.lineHeight) || 24;
        const fontSize = parseInt(style.fontSize) || 16;
        
        // Get text before cursor to calculate line and column
        const textBefore = value.substring(0, cursorPos);
        const lines = textBefore.split('\n');
        const currentLine = lines.length - 1;
        const currentLineText = lines[currentLine] || '';
        
        // Calculate approximate position
        // This is an approximation - for more accuracy, we'd need to measure text width
        const charWidth = fontSize * 0.6; // Approximate character width
        const rawTop = paddingTop + (currentLine + 1) * lineHeight - textarea.scrollTop;
        const rawLeft = paddingLeft + currentLineText.length * charWidth - textarea.scrollLeft;
        const popupWidth = 320;
        const popupHeight = 260;
        const minPadding = 8;
        const maxLeft = Math.max(minPadding, textarea.clientWidth - popupWidth - minPadding);
        const maxTop = Math.max(minPadding, textarea.clientHeight - popupHeight - minPadding);
        const top = Math.min(Math.max(rawTop, minPadding), maxTop);
        const left = Math.min(Math.max(rawLeft, minPadding), maxLeft);
        
        setAutocompletePosition({ top, left });
      }
    } else {
      setAutocompleteOpen(false);
      setAutocompleteQuery('');
      setLinkStartIndex(-1);
    }
  };
  
  // Handle autocomplete selection
  const handleAutocompleteSelect = (linkText) => {
    if (!editorRef.current || linkStartIndex === -1) return;
    
    const textarea = editorRef.current;
    const currentValue = page?.content || '';
    const textBefore = currentValue.substring(0, linkStartIndex);
    const textAfter = currentValue.substring(textarea.selectionStart);
    const newValue = textBefore + `[[${linkText}]]` + textAfter;

    if (currentValue !== newValue) pushUndoSnapshot(currentValue);
    setPageContent(selectedSection, selectedPage, newValue);
    setAutocompleteOpen(false);
    setAutocompleteQuery('');
    setLinkStartIndex(-1);
    
    // Set cursor position after the inserted link
    setTimeout(() => {
      if (editorRef.current) {
        const newCursorPos = textBefore.length + linkText.length + 4; // +4 for [[ and ]]
        editorRef.current.setSelectionRange(newCursorPos, newCursorPos);
        editorRef.current.focus();
      }
    }, 0);
  };

  const handleUndo = () => {
    if (!selectedSection || !selectedPage) return;
    const undoStack = undoStackRef.current;
    if (!undoStack.length) return;
    const previousValue = undoStack.pop();
    setCanUndo(undoStack.length > 0);
    setPageContent(selectedSection, selectedPage, previousValue);
    setAutocompleteOpen(false);
    setAutocompleteQuery('');
    setLinkStartIndex(-1);
    setTimeout(() => {
      if (editorRef.current) {
        const pos = previousValue.length;
        editorRef.current.focus();
        editorRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };
  
  // Get current section/page
  const section = sections.find(s => s.id === selectedSection);
  const page = section?.pages.find(p => p.id === selectedPage);
  const visibleRecentPages = resolveRecentPages(recentPages, sections, page ? selectedPage : null);
  const pageBreadcrumb = section && page
    ? [
        section.parentId ? sections.find((candidate) => candidate.id === section.parentId)?.name : null,
        section.name || 'Untitled Section',
        page.name || 'Untitled',
      ].filter(Boolean)
    : [];

  // Pages linking here with [[...]]. Deferred so typing never waits on the scan.
  const deferredSections = useDeferredValue(sections);
  const backlinks = useMemo(
    () => (selectedPage ? findBacklinks(deferredSections, selectedPage, parseWikiLinks, findPageByName) : []),
    [deferredSections, selectedPage]
  );

  // Focus mode hides the notebook sidebar on desktop (phones already use a drawer).
  const focusModeActive = focusMode && !isMobile;
  const setFocusModePersisted = (next) => {
    setFocusMode(next);
    if (currentUser?.uid) {
      try { localStorage.setItem(getFocusModeKey(currentUser.uid), String(next)); } catch (e) { /* storage unavailable */ }
    }
  };
  // Esc leaves focus mode unless it is closing something else first.
  const focusEscBlockedRef = useRef(false);
  focusEscBlockedRef.current = Boolean(
    selectionPosition || autocompleteOpen || observationLinkPicker || showGalleryModal || showLinkHelp
    || colorPickerOpen || pageColorPickerOpen || editingPage || editingSection
  );
  useEffect(() => {
    if (!focusModeActive) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || focusEscBlockedRef.current) return;
      setFocusMode(false);
      if (currentUser?.uid) {
        try { localStorage.setItem(getFocusModeKey(currentUser.uid), 'false'); } catch (e) { /* storage unavailable */ }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [focusModeActive, currentUser?.uid]);
  const observationDestinationPages = getAllPages(sections)
    .filter((candidate) => String(candidate.pageId) !== String(observationLinkPicker?.sourcePageId || ''))
    .filter((candidate) => {
      const query = observationLinkQuery.trim().toLowerCase();
      return !query || candidate.searchText.includes(query);
    });

  // Search filter
  const filteredSections = sections.map(sec => {
    if (!searchQuery.trim()) return sec;
    
    const sectionNameMatch = sec.name.toLowerCase().includes(searchQuery.toLowerCase());
    const filteredPages = sec.pages.filter(p => 
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.content.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    if (sectionNameMatch || filteredPages.length > 0) {
      return { ...sec, pages: sectionNameMatch ? sec.pages : filteredPages, collapsed: false };
    }
    return null;
  }).filter(Boolean);

  // Hierarchy organization
  const topLevelItems = filteredSections.filter(s => !s.parentId);
  const getSectionsByParent = (parentId) => 
    filteredSections.filter(s => s.parentId === parentId);

  // Clear highlighted image when switching pages
  useEffect(() => {
    if (selectionToolbarTimeoutRef.current) {
      clearTimeout(selectionToolbarTimeoutRef.current);
      selectionToolbarTimeoutRef.current = null;
    }
    setIsEditorFocused(false);
    setSelectionPosition(null);
    setHighlightedImageId(null);
    undoStackRef.current = [];
    setCanUndo(false);
  }, [selectedPage, selectedSection]);

  useEffect(() => {
    if (isMobile && selectedPage) {
      setIsMobileSidebarOpen(false);
      setShowAddMenu(false);
    }
  }, [isMobile, selectedPage]);

  useEffect(() => {
    return () => {
      if (selectionToolbarTimeoutRef.current) {
        clearTimeout(selectionToolbarTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    if (isMobileSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, isMobileSidebarOpen]);

  // Clear highlight when clicking outside textarea
  const clearHighlight = () => {
    setHighlightedImageId(null);
  };

  const hasAnyPage = sections.some((s) => (s.pages || []).length > 0);
  const syncLabel = (() => {
    if (syncStatus === 'queued') return 'Saved locally · syncing';
    if (syncStatus === 'saving') return 'Saving...';
    if (syncStatus === 'offline') return 'Offline (saved locally)';
    if (syncStatus === 'error') return 'Sync error (retrying)';
    if (syncStatus === 'conflict') return 'Sync conflict!';
    if (syncStatus === 'doc_too_large') return 'Notebook too large!';
    if (lastSavedAt) return `Saved ${new Date(lastSavedAt).toLocaleTimeString()}`;
    return 'Saved';
  })();

  // Show loading state to prevent flash
  if (isLoading) {
    return (
      <div className="flex w-full min-h-screen bg-[#FAFAFA] dark:bg-[#111827] items-center justify-center">
        <div className="text-[#6B7280] dark:text-[#9CA3AF]">Loading notebook...</div>
      </div>
    );
  }

  const recoverOlderLocalEdits = async () => {
    if (!currentUser?.uid) return;
    setSyncStatus('saving');
    try {
      const recovery = await notebookSync.claimLegacyWAL(currentUser.uid);
      const claimed = recovery?.count || 0;
      const {
        meta,
        pages,
        legacyWalCount: remainingLegacy = 0
      } = await notebookSync.loadFromCacheAndWAL(currentUser.uid);
      const recoveredSections = markLoadedSections(buildSectionsFromMetaAndPages(meta, pages));

      isHydratingRef.current = true;
      prevSectionsRef.current = recoveredSections;
      sectionsRef.current = recoveredSections;
      setSections(recoveredSections);
      setLegacyWalCount(remainingLegacy);
      hasLocalEditsRef.current = claimed > 0;
      setHasPendingUnsaved(claimed > 0);
      setSyncStatus(
        claimed > 0
          ? (navigator.onLine ? 'queued' : 'offline')
          : (navigator.onLine ? 'saved' : 'offline')
      );
      window.setTimeout(() => {
        isHydratingRef.current = false;
      }, 0);
    } catch (error) {
      console.error('[NOTEBOOK] Could not recover older local edits:', error);
      setSyncStatus('error');
      setConflictNotice({
        title: 'Recovery failed',
        message: 'The older local edits are still preserved in this browser. Nothing was deleted; try again before clearing browser data.',
        showReload: false,
        showOverwrite: false
      });
    }
  };

  const restoreFromCloud = async () => {
    if (!currentUser?.uid) return;
    // Clear WAL and reload from cloud
    await notebookSync.clearLocalData(currentUser.uid);
    const remoteSections = lastRemoteSectionsRef.current;
    isHydratingRef.current = true;
    setSections(markLoadedSections(Array.isArray(remoteSections) ? remoteSections : []));
    setTimeout(() => { isHydratingRef.current = false; }, 0);
    setConflictNotice(null);
    setSyncStatus(navigator.onLine ? 'saved' : 'offline');
  };

  // Force overwrite cloud with local data (user explicitly chose this)
  const forceOverwriteCloud = async () => {
    if (!currentUser?.uid) return;
    setConflictNotice(null);
    setSyncStatus('saving');

    try {
      const metaSections = sections.map(s => ({
        id: s.id,
        name: s.name,
        color: s.color,
        collapsed: s.collapsed,
        isFolder: s.isFolder,
        parentId: s.parentId,
        pageIds: s.pages.map(p => p.id)
      }));

      const metaResult = await notebookSync.forceOverwriteMeta(
        currentUser.uid,
        { sections: metaSections }
      );
      if (!metaResult?.success) {
        throw metaResult?.error || new Error('meta_force_overwrite_failed');
      }

      for (const sec of sections) {
        for (const page of sec.pages) {
          // A placeholder has no local data; writing it would blank the cloud page.
          if (isPlaceholderPage(page)) continue;
          const pageResult = await notebookSync.forceOverwritePage(currentUser.uid, page.id, {
            sectionId: sec.id,
            name: page.name,
            content: page.content || '',
            stickyNotes: page.stickyImages ?? page.stickyNotes ?? [],
            color: page.color ?? null
          });
          if (!pageResult?.success) {
            throw pageResult?.error || new Error(`page_force_overwrite_failed:${page.id}`);
          }
        }
      }

      // Clear recovery data only after every cloud write is confirmed.
      await notebookSync.clearLocalData(currentUser.uid);
      hasLocalEditsRef.current = false;
      setHasPendingUnsaved(false);
      setSyncStatus('saved');
      setLastSavedAt(Date.now());
    } catch (error) {
      console.error('[NOTEBOOK] Force overwrite failed; local recovery data was preserved:', error);
      hasLocalEditsRef.current = true;
      setHasPendingUnsaved(true);
      setSyncStatus('error');
      setConflictNotice({
        title: 'Overwrite failed',
        message: 'The cloud overwrite did not finish, so your local recovery copy was kept. Check your connection and try again.',
        showReload: false,
        showOverwrite: true
      });
    }
  };
  
  const performStrictManualSave = async () => {
    if (manualSaveInFlight) return;
    setManualSaveInFlight(true);
    setSyncStatus('saving');
    setHasPendingUnsaved(true);
    hasLocalEditsRef.current = true;
    try {
      const result = await notebookSync.flushUntilSaved({ timeoutMs: 30000, pollMs: 200 });
      if (!result?.success) {
        throw new Error(result?.reason || 'manual_save_failed');
      }
    } finally {
      setManualSaveInFlight(false);
    }
  };

  // Retry save manually
  const retrySaveNow = async () => {
    try {
      await performStrictManualSave();
    } catch (error) {
      console.error('[NOTEBOOK] Retry save failed:', error);
      setSyncStatus('error');
      setConflictNotice({
        title: 'Save failed',
        message: 'Manual retry could not confirm a cloud save. Your changes are still kept locally and will continue retrying.',
        showReload: false,
        showOverwrite: false
      });
    }
  };

  const handleSaveNow = async () => {
    try {
      await performStrictManualSave();
    } catch (error) {
      console.error('[NOTEBOOK] Save now failed:', error);
      setSyncStatus('error');
      setConflictNotice({
        title: 'Save failed',
        message: 'Could not confirm save completion. Changes remain local and automatic retry is still active.',
        showReload: false,
        showOverwrite: false
      });
    }
  };

  // ---- Sidebar rendering -------------------------------------------------
  const searchActive = Boolean(searchQuery.trim());
  const pinnedIdSet = new Set(pinnedPageIds.map(String));
  const pinnedRows = resolvePinnedPages(pinnedPageIds, sections);

  const togglePin = (pageId) => {
    setPinnedPageIds((prev) => {
      const next = togglePinnedPage(prev, pageId);
      if (currentUser?.uid) {
        try { localStorage.setItem(getPinnedKey(currentUser.uid), JSON.stringify(next)); } catch (e) { /* storage unavailable */ }
      }
      return next;
    });
  };

  // Row actions show on hover / keyboard focus. Touch screens have no hover, so
  // there they show on the selected row only.
  const rowActionsClass = (isSelected) => `flex shrink-0 items-center opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 ${
    isSelected ? '[@media(hover:none)]:opacity-100' : '[@media(hover:none)]:hidden'
  }`;
  const ROW_ACTION_BUTTON = "rounded p-1 text-[#9CA3AF] transition-colors hover:bg-[#E5E7EB] hover:text-[#111827] focus-visible:opacity-100 dark:text-[#6B7280] dark:hover:bg-[#4B5563]/60 dark:hover:text-[#F9FAFB]";

  const renderColorPicker = (currentColor, onPick, extraClass = '') => (
    <div className={`flex flex-wrap items-center gap-1.5 px-2 py-1.5 ${extraClass}`} onClick={(e) => e.stopPropagation()}>
      {SECTION_COLORS.map((color) => (
        <button
          key={color.value}
          type="button"
          className={`h-5 w-5 rounded-full transition-transform hover:scale-110 ${
            (currentColor || null) === color.value ? 'ring-2 ring-offset-1 ring-[#3B82F6] ring-offset-white dark:ring-offset-[#1F2937]' : ''
          }`}
          style={{ backgroundColor: color.value }}
          onClick={() => onPick(color.value)}
          title={color.name}
          aria-label={color.name}
        />
      ))}
    </div>
  );

  const renderPageList = (sec) => (
    <div className="ml-[13px] border-l border-[#E5E7EB] pl-1.5 dark:border-[#374151]">
      {sec.pages.map((p, pageIndex) => {
        const isSelected = selectedPage === p.id;
        const isMissing = p.name === 'Loading...' && !p.content;
        const accent = p.color || sec.color || '#3b82f6';
        const isPinned = pinnedIdSet.has(String(p.id));
        const showLineBefore = dragOverPageIndex?.sectionId === sec.id && dragOverPageIndex.pageIndex === pageIndex;
        const showLineAfter = pageIndex === sec.pages.length - 1
          && dragOverPageIndex?.sectionId === sec.id && dragOverPageIndex.pageIndex === pageIndex + 1;
        return (
          <React.Fragment key={p.id}>
            <div
              className={`group relative flex min-h-[28px] items-center gap-1 rounded px-1.5 py-0.5 ${
                isSelected ? '' : 'hover:bg-[#F3F4F6] dark:hover:bg-[#374151]/60'
              } ${draggedPage?.pageId === p.id ? 'opacity-50' : ''} ${searchActive ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'}`}
              style={isSelected ? { backgroundColor: `${accent}24` } : undefined}
              draggable={!searchActive && editingPage !== p.id}
              onDragStart={(e) => handleDragStart(e, sec.id, p.id)}
              onDragOver={(e) => handlePageElementDragOver(e, sec.id, pageIndex)}
              onDrop={(e) => handlePageElementDrop(e, sec.id, pageIndex)}
              onDragEnd={handleDragEnd}
              onClick={() => { setSelectedSection(sec.id); setSelectedPage(p.id); }}
            >
              {showLineBefore && (
                <span aria-hidden="true" className="pointer-events-none absolute -top-px left-1 right-1 h-0.5 rounded-full bg-[#3B82F6]" />
              )}
              {showLineAfter && (
                <span aria-hidden="true" className="pointer-events-none absolute -bottom-px left-1 right-1 h-0.5 rounded-full bg-[#3B82F6]" />
              )}
              {isMissing ? (
                <>
                  <span className="flex-1 text-sm italic text-[#EF4444] opacity-70 dark:text-[#F87171]">Missing page</span>
                  <button type="button" className={ROW_ACTION_BUTTON} onClick={(e) => { e.stopPropagation(); deletePage(sec.id, p.id); }} title="Remove missing page" aria-label="Remove missing page">
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                </>
              ) : editingPage === p.id ? (
                <input
                  className="min-w-0 flex-1 border-b bg-transparent text-sm outline-none"
                  style={{ borderBottomColor: accent }}
                  value={p.name}
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => renamePage(sec.id, p.id, e.target.value)}
                  onBlur={() => setEditingPage(null)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditingPage(null); }}
                />
              ) : (
                <>
                  <button
                    type="button"
                    className={`min-w-0 flex-1 text-left text-sm leading-5 ${
                      isSelected ? 'whitespace-normal break-words font-medium text-[#111827] dark:text-[#F9FAFB]' : 'truncate text-[#374151] dark:text-[#D1D5DB]'
                    }`}
                    style={p.color ? { color: p.color } : undefined}
                    onDoubleClick={() => setEditingPage(p.id)}
                    title={p.name}
                    aria-current={isSelected ? 'page' : undefined}
                  >
                    {p.name}
                  </button>
                  <div className={rowActionsClass(isSelected)}>
                    <button
                      type="button"
                      className={`${ROW_ACTION_BUTTON} ${isPinned ? '!text-[#3B82F6]' : ''}`}
                      onClick={(e) => { e.stopPropagation(); togglePin(p.id); }}
                      title={isPinned ? 'Unpin page' : 'Pin page'}
                      aria-label={isPinned ? `Unpin ${p.name}` : `Pin ${p.name}`}
                      aria-pressed={isPinned}
                    >
                      {isPinned ? <PinOff className="h-3.5 w-3.5" strokeWidth={2} /> : <Pin className="h-3.5 w-3.5" strokeWidth={2} />}
                    </button>
                    <button
                      type="button"
                      className={ROW_ACTION_BUTTON}
                      onClick={(e) => { e.stopPropagation(); setPageColorPickerOpen(pageColorPickerOpen === p.id ? null : p.id); }}
                      title="Change Page Color"
                      aria-label={`Change colour of ${p.name}`}
                    >
                      <SwatchIcon className="h-3.5 w-3.5" style={{ color: accent }} />
                    </button>
                    <button type="button" className={ROW_ACTION_BUTTON} onClick={(e) => { e.stopPropagation(); setEditingPage(p.id); }} title="Rename Page" aria-label={`Rename ${p.name}`}>
                      <PencilIcon className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" className={`${ROW_ACTION_BUTTON} hover:!text-[#EF4444]`} onClick={(e) => { e.stopPropagation(); deletePage(sec.id, p.id); }} title="Delete Page" aria-label={`Delete ${p.name}`}>
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </>
              )}
            </div>
            {pageColorPickerOpen === p.id && renderColorPicker(p.color, (color) => changePageColor(sec.id, p.id, color))}
          </React.Fragment>
        );
      })}
      <button
        type="button"
        className="mb-1 ml-1.5 mt-0.5 flex items-center gap-1 rounded px-0.5 text-xs text-[#6B7280] transition-colors hover:text-[#3B82F6] dark:text-[#9CA3AF] dark:hover:text-[#60A5FA]"
        onClick={() => addPage(sec.id)}
      >
        <PlusIcon className="h-3.5 w-3.5" /> New Page
      </button>
    </div>
  );

  const renderSectionNameOrInput = (sec, onSelect) => (
    editingSection === sec.id ? (
      <input
        className="min-w-0 flex-1 border-b bg-transparent text-sm font-semibold outline-none text-[#111827] dark:text-[#F9FAFB]"
        style={{ borderBottomColor: sec.color }}
        value={sec.name}
        autoFocus
        onChange={(e) => renameSection(sec.id, e.target.value)}
        onBlur={() => setEditingSection(null)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditingSection(null); }}
      />
    ) : (
      <button
        type="button"
        className="min-w-0 flex-1 truncate text-left text-sm font-semibold text-[#111827] dark:text-[#F9FAFB]"
        onClick={onSelect}
        onDoubleClick={() => setEditingSection(sec.id)}
        title={sec.name}
      >
        {sec.name}
      </button>
    )
  );

  const renderCollapseToggle = (sec) => (
    <button
      type="button"
      onClick={() => toggleSectionCollapse(sec.id)}
      className="shrink-0 rounded p-0.5 text-[#9CA3AF] hover:bg-[#E5E7EB] hover:text-[#111827] dark:text-[#6B7280] dark:hover:bg-[#4B5563]/60 dark:hover:text-[#F9FAFB]"
      title={sec.collapsed ? 'Expand' : 'Collapse'}
      aria-expanded={!sec.collapsed}
      aria-label={`${sec.collapsed ? 'Expand' : 'Collapse'} ${sec.name}`}
    >
      {sec.collapsed ? <ChevronRightIcon className="h-3.5 w-3.5" /> : <ChevronDownIcon className="h-3.5 w-3.5" />}
    </button>
  );

  // A section (top level or inside a folder) with its pages.
  const renderSection = (sec, { nested = false } = {}) => {
    const isSelected = selectedSection === sec.id;
    const selectSection = () => {
      setSelectedSection(sec.id);
      if (sec.pages.length > 0 && !sec.collapsed) setSelectedPage(sec.pages[0].id);
    };
    return (
      <div
        key={sec.id}
        className={`mb-1 rounded-md ${draggedSection === sec.id ? 'opacity-50' : ''}`}
        draggable={!searchActive && editingSection !== sec.id}
        onDragStart={(e) => handleSectionDragStart(e, sec.id)}
        onDragOver={handleSectionContainerDragOver}
        onDrop={(e) => handleDrop(e, sec.id)}
        onDragEnd={handleDragEnd}
      >
        <div className={`group flex min-h-[30px] items-center gap-1 rounded-md px-1 ${isSelected && !selectedPage ? 'bg-[#F3F4F6] dark:bg-[#374151]/60' : 'hover:bg-[#F3F4F6] dark:hover:bg-[#374151]/60'}`}>
          {renderCollapseToggle(sec)}
          <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: sec.color || '#3b82f6' }} />
          {renderSectionNameOrInput(sec, selectSection)}
          <div className={rowActionsClass(isSelected)}>
            <button
              type="button"
              className={ROW_ACTION_BUTTON}
              onClick={(e) => { e.stopPropagation(); setColorPickerOpen(colorPickerOpen === sec.id ? null : sec.id); }}
              title="Change Color"
              aria-label={`Change colour of ${sec.name}`}
            >
              <SwatchIcon className="h-3.5 w-3.5" style={{ color: sec.color }} />
            </button>
            <button type="button" className={ROW_ACTION_BUTTON} onClick={(e) => { e.stopPropagation(); setEditingSection(sec.id); }} title="Rename Section" aria-label={`Rename ${sec.name}`}>
              <PencilIcon className="h-3.5 w-3.5" />
            </button>
            <button type="button" className={`${ROW_ACTION_BUTTON} hover:!text-[#EF4444]`} onClick={(e) => { e.stopPropagation(); deleteSection(sec.id); }} title="Delete Section" aria-label={`Delete ${sec.name}`}>
              <TrashIcon className="h-3.5 w-3.5" />
            </button>
            {nested && (
              <button
                type="button"
                className={`${ROW_ACTION_BUTTON} hover:!text-[#F59E0B]`}
                onClick={(e) => { e.stopPropagation(); setSections((s) => s.map((x) => (x.id === sec.id ? { ...x, parentId: null } : x))); }}
                title="Move to root level"
                aria-label={`Move ${sec.name} out of the folder`}
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
        {colorPickerOpen === sec.id && renderColorPicker(sec.color, (color) => changeSectionColor(sec.id, color))}
        {!sec.collapsed && renderPageList(sec)}
      </div>
    );
  };

  const renderFolder = (folder) => {
    const isSelected = selectedSection === folder.id;
    return (
      <div
        key={folder.id}
        className={`mb-1 rounded-md ${draggedSection ? 'outline-dashed outline-1 outline-[#60A5FA]' : ''}`}
        onDragOver={(e) => handleSectionDragOver(e, folder.id)}
        onDrop={(e) => handleSectionDrop(e, folder.id)}
      >
        <div className={`group flex min-h-[30px] items-center gap-1 rounded-md px-1 ${isSelected ? 'bg-[#F3F4F6] dark:bg-[#374151]/60' : 'hover:bg-[#F3F4F6] dark:hover:bg-[#374151]/60'}`}>
          {renderCollapseToggle(folder)}
          <FolderIcon className="h-4 w-4 shrink-0" style={{ color: folder.color }} />
          {renderSectionNameOrInput(folder, () => setSelectedSection(folder.id))}
          <div className={rowActionsClass(isSelected)}>
            <button
              type="button"
              className={ROW_ACTION_BUTTON}
              onClick={(e) => { e.stopPropagation(); setColorPickerOpen(colorPickerOpen === folder.id ? null : folder.id); }}
              title="Change Color"
              aria-label={`Change colour of ${folder.name}`}
            >
              <SwatchIcon className="h-3.5 w-3.5" style={{ color: folder.color }} />
            </button>
            <button type="button" className={ROW_ACTION_BUTTON} onClick={(e) => { e.stopPropagation(); setEditingSection(folder.id); }} title="Rename Folder" aria-label={`Rename ${folder.name}`}>
              <PencilIcon className="h-3.5 w-3.5" />
            </button>
            <button type="button" className={`${ROW_ACTION_BUTTON} hover:!text-[#EF4444]`} onClick={(e) => { e.stopPropagation(); deleteSection(folder.id); }} title="Delete Folder" aria-label={`Delete ${folder.name}`}>
              <TrashIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        {colorPickerOpen === folder.id && renderColorPicker(folder.color, (color) => changeSectionColor(folder.id, color))}
        {!folder.collapsed && (
          <div className="ml-[13px] border-l border-[#E5E7EB] pl-1.5 dark:border-[#374151]">
            {getSectionsByParent(folder.id).map((child) => renderSection(child, { nested: true }))}
            <button
              type="button"
              className="mb-1 ml-1.5 mt-0.5 flex items-center gap-1 rounded px-0.5 text-xs text-[#6B7280] transition-colors hover:text-[#3B82F6] dark:text-[#9CA3AF] dark:hover:text-[#60A5FA]"
              onClick={() => addSectionToFolder(folder.id)}
            >
              <PlusIcon className="h-3.5 w-3.5" /> New Section in Folder
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderQuickList = ({ label, rows, collapsed, onToggle, renderTrailing }) => (
    <div className={`mb-3 ${sidebarLayoutExpanded ? 'col-span-3' : ''}`}>
      <button
        type="button"
        className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[#6B7280] hover:text-[#111827] dark:text-[#9CA3AF] dark:hover:text-[#F9FAFB]"
        onClick={onToggle}
        aria-expanded={onToggle ? !collapsed : undefined}
        disabled={!onToggle}
      >
        {onToggle ? (collapsed ? <ChevronRightIcon className="h-3 w-3" /> : <ChevronDownIcon className="h-3 w-3" />) : null}
        {label}
      </button>
      {!collapsed && (
        <ul className="mt-0.5">
          {rows.map((row) => (
            <li key={row.pageId} className="group flex items-center">
              <button
                type="button"
                className={`flex min-w-0 flex-1 items-baseline gap-1.5 rounded px-2 py-1 text-left text-[13px] leading-5 hover:bg-[#F3F4F6] dark:hover:bg-[#374151] md:py-0.5 ${
                  String(row.pageId) === String(selectedPage) ? 'font-medium text-[#111827] dark:text-[#F9FAFB]' : 'text-[#374151] dark:text-[#D1D5DB]'
                }`}
                onClick={() => {
                  setSelectedSection(row.sectionId);
                  setSelectedPage(row.pageId);
                }}
                title={row.sectionName ? `${row.sectionName} › ${row.pageName}` : row.pageName}
              >
                <span className="min-w-0 truncate">{row.pageName}</span>
                {row.sectionName ? (
                  <span className="min-w-0 shrink-[2] truncate text-[11px] text-[#9CA3AF] dark:text-[#6B7280]">{row.sectionName}</span>
                ) : null}
              </button>
              {renderTrailing ? renderTrailing(row) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <div className="flex flex-col md:flex-row w-full min-h-screen bg-[#FAFAFA] dark:bg-[#111827] text-[#111827] dark:text-[#F9FAFB] relative pt-14 md:pt-0" onClick={clearHighlight}>
      {hasLoadError && (
        <div className="absolute top-4 left-4 right-4 z-[60] bg-[#FEF2F2] dark:bg-[#7F1D1D]/60 border border-[#FCA5A5] dark:border-[#B91C1C] text-[#991B1B] dark:text-[#FCA5A5] px-4 py-3 rounded-card text-sm shadow-subtle">
          Couldn’t reach your notebook right now. Your notes are kept locally and will sync when the connection returns.
        </div>
      )}
      {conflictNotice && (
        <div className="absolute top-4 left-4 right-4 z-[61] bg-[#FFFBEB] dark:bg-[#78350F]/40 border border-[#FDE68A] dark:border-[#D97706] text-[#92400E] dark:text-[#FBBF24] px-4 py-3 rounded-card text-sm shadow-subtle">
          <div className="font-semibold mb-1">{conflictNotice.title}</div>
          <div className="mb-2">{conflictNotice.message}</div>
          <div className="flex flex-wrap gap-2">
            {conflictNotice.showReload !== false && (
              <button
                type="button"
                onClick={restoreFromCloud}
                className="px-3 py-2 rounded-card bg-[#3B82F6] hover:bg-[#2563EB] text-white text-sm font-medium transition-colors"
              >
                Reload from Cloud
              </button>
            )}
            {conflictNotice.showOverwrite && (
              <button
                type="button"
                onClick={forceOverwriteCloud}
                className="px-3 py-2 rounded-card bg-[#F59E0B] hover:bg-[#D97706] text-white text-sm font-medium transition-colors"
              >
                Keep Local & Overwrite
              </button>
            )}
            {syncStatus === 'error' && (
              <button
                type="button"
                onClick={retrySaveNow}
                disabled={manualSaveInFlight}
                className="px-3 py-2 rounded-card bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] text-[#111827] dark:text-[#F9FAFB] text-sm font-medium hover:bg-[#F3F4F6] dark:hover:bg-[#374151] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                Retry Save
              </button>
            )}
            <button
              type="button"
              onClick={() => setConflictNotice(null)}
              className="px-3 py-2 rounded-card text-sm font-medium underline hover:opacity-80 transition-opacity"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      {legacyWalCount > 0 && (
        <div className="fixed bottom-4 left-4 right-4 z-[70] rounded-card border border-[#C4B5FD] bg-[#F5F3FF] px-4 py-3 text-sm text-[#5B21B6] shadow-xl md:left-auto md:w-[32rem] dark:border-[#7C3AED] dark:bg-[#2E1065]/95 dark:text-[#DDD6FE]">
          <div className="font-semibold">Older local edits found</div>
          <p className="mt-1 text-xs leading-5">
            {legacyWalCount} pending {legacyWalCount === 1 ? 'edit was' : 'edits were'} created before saves were account-scoped.
            Recover only if {legacyWalCount === 1 ? 'it belongs' : 'they belong'} to the account signed in now.
          </p>
          <button
            type="button"
            onClick={recoverOlderLocalEdits}
            className="mt-2 rounded-card bg-[#7C3AED] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#6D28D9]"
          >
            Recover for this account
          </button>
        </div>
      )}
      {observationLinkPicker && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm"
          role="presentation"
          onMouseDown={closeObservationLinkPicker}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="observation-link-title"
            className="theme-panel theme-border flex max-h-[72vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border shadow-2xl"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="theme-border flex items-start justify-between gap-4 border-b px-4 py-3.5">
              <div>
                <h2 id="observation-link-title" className="text-base font-semibold theme-text">Link observation</h2>
                <p className="mt-0.5 text-xs theme-text-muted">Choose the page where this synced observation should appear.</p>
              </div>
              <button
                type="button"
                onClick={closeObservationLinkPicker}
                className="theme-hover-surface rounded-lg p-1.5 theme-text-muted"
                aria-label="Close observation page picker"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="px-4 pt-3">
              <div className="theme-panel-alt theme-border rounded-xl border border-l-4 px-3 py-2.5 text-sm leading-6 theme-text" style={{ borderLeftColor: 'var(--theme-accent)' }}>
                {observationLinkPicker.selectedText}
              </div>
              <div className="relative mt-3">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 theme-text-muted" />
                <input
                  autoFocus
                  type="search"
                  value={observationLinkQuery}
                  onChange={(event) => {
                    setObservationLinkQuery(event.target.value);
                    setObservationLinkError('');
                  }}
                  placeholder="Search Notebook pages..."
                  className="theme-panel-alt theme-border h-10 w-full rounded-xl border pl-9 pr-3 text-sm theme-text outline-none focus:ring-2 focus:ring-[var(--theme-accent)]"
                />
              </div>
              {observationLinkError ? (
                <p className="mt-2 text-xs text-[#EF4444]">{observationLinkError}</p>
              ) : null}
            </div>

            <div className="mt-3 min-h-0 flex-1 overflow-y-auto px-2 pb-3">
              {observationDestinationPages.length > 0 ? observationDestinationPages.map((destination) => (
                <button
                  key={`${destination.sectionId}-${destination.pageId}`}
                  type="button"
                  onClick={() => linkObservationToPage(destination)}
                  className="theme-hover-surface flex w-full items-center justify-between gap-4 rounded-xl px-3 py-2.5 text-left"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold theme-text">{destination.pageName}</span>
                    <span className="mt-0.5 block truncate text-xs theme-text-muted">
                      {[destination.folderName, destination.sectionName].filter(Boolean).join(' / ')}
                    </span>
                  </span>
                  <ChevronRightIcon className="h-4 w-4 shrink-0 theme-text-muted" />
                </button>
              )) : (
                <div className="px-4 py-8 text-center text-sm theme-text-muted">
                  No other Notebook pages match that search.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Mobile Top Bar */}
      <div className="md:hidden fixed top-14 left-0 right-0 z-[65] h-14 bg-white dark:bg-[#1F2937] border-b border-[#E5E7EB] dark:border-[#374151] flex items-center justify-between px-4">
        <button
          onClick={() => setIsMobileSidebarOpen(true)}
          className="h-10 px-2 flex items-center gap-1.5 rounded-lg border border-[#E5E7EB] dark:border-[#374151] hover:bg-[#F3F4F6] dark:hover:bg-[#374151] transition-colors text-sm font-medium text-[#111827] dark:text-[#F9FAFB]"
          aria-label="Open notebook pages"
        >
          <BookOpenIcon className="w-5 h-5 text-[#3B82F6]" />
          Pages
        </button>
        <div className="flex flex-col items-center leading-tight min-h-[2.5rem]">
          <span className="text-sm font-semibold text-[#111827] dark:text-[#F9FAFB]">Notebook</span>
          <span className="text-[11px] text-[#6B7280] dark:text-[#9CA3AF] whitespace-nowrap tabular-nums">{syncLabel}</span>
        </div>
        <button
          onClick={() => {
            setIsMobileSidebarOpen(true);
            setShowAddMenu(true);
          }}
          className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#3B82F6] text-white hover:bg-[#2563EB] transition-colors"
          aria-label="Add notebook item"
        >
          <PlusIcon className="w-5 h-5" />
        </button>
      </div>

      {isMobile && isMobileSidebarOpen && (
        <div
          className="md:hidden fixed inset-0 z-[71] bg-black/40 backdrop-blur-sm"
          onClick={() => {
            setIsMobileSidebarOpen(false);
            setShowAddMenu(false);
          }}
        />
      )}
      {/* Sidebar */}
      <aside
        className={`${
          isMobile
            ? `fixed top-0 left-0 h-full w-72 sm:w-80 bg-white dark:bg-[#1F2937] border-r border-[#E5E7EB] dark:border-[#374151] flex flex-col p-4 gap-4 shadow-subtle transform transition-transform duration-300 z-[72] ${
                isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
              }`
            : `${focusModeActive ? 'hidden' : 'flex'} ${sidebarLayoutExpanded ? 'w-[900px]' : 'w-80'} min-w-[16rem] bg-white dark:bg-[#1F2937] border-r border-[#E5E7EB] dark:border-[#374151] flex-col p-4 gap-4 transition-all duration-300 ease-in-out shadow-subtle`
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-2 min-h-[2.5rem]">
          <BookOpenIcon className="w-7 h-7 shrink-0 text-[#3B82F6]" />
          <span className="shrink-0 text-xl font-semibold tracking-tight text-[#111827] dark:text-[#F9FAFB]">Notebook</span>
          <SaveStatusIndicator
            syncStatus={syncStatus}
            syncLabel={syncLabel}
            hasPendingUnsaved={hasPendingUnsaved}
            manualSaveInFlight={manualSaveInFlight}
            onSaveNow={handleSaveNow}
          />
          <button
            type="button"
            onClick={() => setFocusModePersisted(true)}
            className="ml-auto hidden shrink-0 rounded-card p-1.5 text-[#6B7280] transition-colors hover:bg-[#F3F4F6] hover:text-[#111827] dark:text-[#9CA3AF] dark:hover:bg-[#374151] dark:hover:text-[#F9FAFB] md:inline-flex"
            title="Focus mode: hide the sidebar (Esc to exit)"
            aria-label="Enter focus mode"
          >
            <ArrowsPointingOutIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => {
              setSidebarExpanded(!sidebarExpanded);
              if (currentUser?.uid) {
                localStorage.setItem(getSidebarExpandedKey(currentUser.uid), String(!sidebarExpanded));
              }
            }}
            className="shrink-0 p-1.5 hover:bg-[#F3F4F6] dark:hover:bg-[#374151] rounded-card transition-colors text-[#111827] dark:text-[#F9FAFB] hidden md:inline-flex"
            title={sidebarExpanded ? "Collapse sidebar" : "Expand sidebar"}
          >
            {sidebarExpanded ? (
              <ChevronDoubleLeftIcon className="w-5 h-5" />
            ) : (
              <ChevronDoubleRightIcon className="w-5 h-5" />
            )}
          </button>
        </div>
        
        {/* Search Bar */}
        <div className="relative">
          <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-[#6B7280] dark:text-[#9CA3AF]" />
          <input
            type="text"
            placeholder="Search notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-8 py-2 bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card text-sm text-[#111827] dark:text-[#F9FAFB] focus:outline-none focus:ring-2 focus:ring-[#3B82F6] transition-all shadow-subtle"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-[#6B7280] dark:text-[#9CA3AF] hover:text-[#111827] dark:text-[#F9FAFB]"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>
        
        <div className="relative">
          <button
            onClick={() => setShowAddMenu(!showAddMenu)}
            className="flex items-center gap-2 px-3 py-2 bg-[#3B82F6] hover:bg-[#2563EB] rounded-card text-white font-medium transition-colors duration-200 w-full shadow-subtle"
          >
            <PlusIcon className="w-5 h-5" /> Add New
          </button>
          {showAddMenu && (
            <div className="absolute top-full mt-1 w-full bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card shadow-elevated z-50">
              <button
                onClick={() => { addSection(); setShowAddMenu(false); }}
                className="w-full px-3 py-2 hover:bg-[#F3F4F6] dark:hover:bg-[#374151] text-left rounded-t-card text-[#111827] dark:text-[#F9FAFB]"
              >
                New Section
              </button>
              <button
                onClick={() => { addFolder(); setShowAddMenu(false); }}
                className="w-full px-3 py-2 hover:bg-[#F3F4F6] dark:hover:bg-[#374151] text-left rounded-b-card text-[#111827] dark:text-[#F9FAFB]"
              >
                New Folder
              </button>
            </div>
          )}
        </div>
        
        <div className={`flex-1 overflow-y-auto pr-1 ${sidebarLayoutExpanded ? 'grid grid-cols-3 gap-2' : 'flex flex-col'}`}>
          {/* Recent Pages */}
          {pinnedRows.length > 0 && !searchActive && renderQuickList({
            label: 'Pinned',
            rows: pinnedRows,
            collapsed: false,
            renderTrailing: (row) => (
              <div className={rowActionsClass(false)}>
                <button
                  type="button"
                  className={ROW_ACTION_BUTTON}
                  onClick={() => togglePin(row.pageId)}
                  title="Unpin page"
                  aria-label={`Unpin ${row.pageName}`}
                >
                  <PinOff className="h-3.5 w-3.5" strokeWidth={2} />
                </button>
              </div>
            ),
          })}
          {visibleRecentPages.length > 0 && !searchActive && renderQuickList({
            label: 'Recent',
            rows: visibleRecentPages,
            collapsed: recentCollapsed,
            onToggle: () => {
              const next = !recentCollapsed;
              setRecentCollapsed(next);
              if (currentUser?.uid) {
                try { localStorage.setItem(getRecentCollapsedKey(currentUser.uid), String(next)); } catch (e) { /* storage unavailable */ }
              }
            },
          })}

          {/* Sections */}
          {topLevelItems.map((item) => (item.isFolder ? renderFolder(item) : renderSection(item)))}
        </div>
      </aside>
      {/* Editor */}
      <main className="relative flex-1 flex flex-col bg-white dark:bg-[#1F2937] py-6 md:py-8 px-4 md:px-0">
        {focusModeActive && !(section && page) && (
          <button
            type="button"
            onClick={() => setFocusModePersisted(false)}
            className="absolute left-4 top-4 rounded-md p-1.5 text-[#6B7280] transition-colors hover:bg-[#F3F4F6] hover:text-[#111827] dark:text-[#9CA3AF] dark:hover:bg-[#374151] dark:hover:text-[#F9FAFB]"
            title="Exit focus mode (Esc)"
            aria-label="Exit focus mode"
          >
            <ArrowsPointingInIcon className="h-5 w-5" />
          </button>
        )}
        {section && page ? (
          <div className={`flex-1 flex flex-col lg:flex-row gap-4 lg:gap-6 max-w-none ${stickyNotesCollapsed ? 'px-4 lg:pl-24 lg:pr-4' : 'px-4 lg:pl-24 lg:pr-0'}`}>
            {/* Text Editor: capped at a comfortable reading width on wide screens */}
            <div className="mx-auto flex w-full min-w-0 max-w-[calc(75ch+2rem+2px)] flex-1 flex-col gap-3">
              <div className="flex min-w-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
                {focusModeActive && (
                  <button
                    type="button"
                    onClick={() => setFocusModePersisted(false)}
                    className="shrink-0 rounded-md p-1.5 text-[#6B7280] transition-colors hover:bg-[#F3F4F6] hover:text-[#111827] dark:text-[#9CA3AF] dark:hover:bg-[#374151] dark:hover:text-[#F9FAFB]"
                    title="Exit focus mode (Esc)"
                    aria-label="Exit focus mode"
                  >
                    <ArrowsPointingInIcon className="h-5 w-5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => navigate('/notebook/graph')}
                  className="p-1 sm:p-2 hover:bg-[#F3F4F6] dark:hover:bg-[#374151] rounded-card transition-all shrink-0"
                  title="View notebook graph"
                  aria-label="Open notebook knowledge map"
                >
                  <ShareIcon className="w-6 h-6 sm:w-8 sm:h-8 text-[#A855F7]" style={{ filter: 'drop-shadow(0 0 4px rgba(168,85,247,0.6))' }} />
                </button>
                <div className="min-w-0 flex-1">
                  <nav aria-label="Breadcrumb" className="truncate text-xs leading-4 text-[#6B7280] dark:text-[#9CA3AF]">
                    {pageBreadcrumb.map((crumb, index) => (
                      <React.Fragment key={`${crumb}-${index}`}>
                        {index > 0 ? <span aria-hidden="true" className="mx-1 opacity-60">›</span> : null}
                        <span className={index === pageBreadcrumb.length - 1 ? 'text-[#374151] dark:text-[#D1D5DB]' : ''}>{crumb}</span>
                      </React.Fragment>
                    ))}
                  </nav>
                <input
                  data-theme-input-mode="unstyled"
                  aria-label="Page title"
                  className="block w-full min-w-0 border-0 bg-transparent p-0 text-[22px] font-bold leading-tight tracking-tight text-[#111827] outline-none sm:text-[26px] lg:text-[28px] dark:text-[#F9FAFB]"
                  value={page.name}
                  readOnly={isPlaceholderPage(page)}
                  onChange={e => renamePage(section.id, page.id, e.target.value)}
                  onFocus={() => { pageTitleFocusedRef.current = true; }}
                  onBlur={() => {
                    pageTitleFocusedRef.current = false;
                    reconcileSyncAfterTitleEditing();
                  }}
                  onClick={clearHighlight}
                />
                </div>
                <div className="relative ml-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPageColorPickerOpen(pageColorPickerOpen === page.id ? null : page.id);
                    }}
                    className="w-6 h-6 flex items-center justify-center rounded-md text-[#6B7280] dark:text-[#9CA3AF] hover:text-[#111827] dark:hover:text-[#F9FAFB] hover:bg-[#F3F4F6] dark:hover:bg-[#374151] transition-colors"
                    title="Change Page Color"
                  >
                    <SwatchIcon className="w-4 h-4" style={{ color: page.color || section.color }} />
                  </button>
                  {pageColorPickerOpen === page.id && (
                    <div
                      className="absolute right-0 top-8 z-[100] bg-gradient-to-b from-white to-[#F9FAFB] dark:from-[#1F2937] dark:to-[#111827] border border-[#E5E7EB] dark:border-[#374151] rounded-lg p-3 shadow-xl"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="grid grid-cols-4 gap-3">
                        {SECTION_COLORS.map(color => (
                          <button
                            key={color.value}
                            className={`relative w-8 h-8 rounded-lg shadow-md hover:shadow-lg hover:scale-110 active:scale-95 transition-all duration-200 border-2 ${
                              (page.color || null) === color.value
                                ? 'ring-2 ring-offset-1 ring-[#3B82F6] border-white dark:border-[#1F2937]'
                                : 'border-transparent hover:border-white dark:hover:border-[#374151]'
                            }`}
                            style={{ backgroundColor: color.value }}
                            onClick={() => changePageColor(section.id, page.id, color.value)}
                            title={color.name}
                          >
                            {(page.color || null) === color.value && (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-3 h-3 bg-white rounded-full shadow-sm" />
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {/* Wiki Link Help Button */}
                <div className="relative ml-2" data-link-help>
                  <button
                    onClick={() => setShowLinkHelp(!showLinkHelp)}
                    className="w-6 h-6 flex items-center justify-center rounded-md text-[#6B7280] dark:text-[#9CA3AF] hover:text-[#111827] dark:hover:text-[#F9FAFB] hover:bg-[#F3F4F6] dark:hover:bg-[#374151] transition-colors"
                    title="Wiki Link Help"
                  >
                    <InformationCircleIcon className="w-4 h-4" />
                  </button>
                  {showLinkHelp && (
                    <div className="absolute right-0 top-8 z-[100] w-72 bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card shadow-elevated p-4 text-sm">
                      <div className="font-semibold text-[#111827] dark:text-[#F9FAFB] mb-2">Notebook Linking</div>
                      <div className="space-y-2 text-[#6B7280] dark:text-[#9CA3AF]">
                        <div>
                          <span className="font-medium text-[#111827] dark:text-[#F9FAFB]">Link a page:</span> Type <code className="bg-[#F3F4F6] dark:bg-[#374151] px-1 rounded">[[Page Name]]</code>
                        </div>
                        <div>
                          <span className="font-medium text-[#111827] dark:text-[#F9FAFB]">Autocomplete:</span> Shows when typing <code className="bg-[#F3F4F6] dark:bg-[#374151] px-1 rounded">[[</code>
                        </div>
                        <div>
                          <span className="font-medium text-[#111827] dark:text-[#F9FAFB]">Link an observation:</span> Highlight one sentence, choose <strong>Link</strong>, then select a destination page
                        </div>
                        <div>
                          <span className="font-medium text-[#111827] dark:text-[#F9FAFB]">Keep it synced:</span> Edit the original sentence and every linked card updates automatically
                        </div>
                        <div>
                          <span className="font-medium text-[#111827] dark:text-[#F9FAFB]">Open or remove:</span> Use the source name to jump back, or remove the card without deleting the original
                        </div>
                      </div>
                      <button
                        onClick={() => setShowLinkHelp(false)}
                        className="mt-3 text-xs text-[#3B82F6] hover:text-[#2563EB] underline"
                      >
                        Close
                      </button>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleUndo}
                  disabled={!canUndo}
                  className="ml-1 inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-[#E5E7EB] bg-white px-2.5 text-xs font-semibold text-[#111827] transition-colors hover:border-[#6B7280] disabled:cursor-not-allowed disabled:opacity-40 dark:border-[#374151] dark:bg-[#111827] dark:text-[#F9FAFB] dark:hover:border-[#9CA3AF]"
                  title="Undo (Ctrl/Cmd+Z)"
                >
                  <ArrowUturnLeftIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Undo</span>
                </button>
              </div>
              <div className="relative flex-1">
                <div
                  className="relative w-full h-full"
                  onDragEnter={handleEditorDragEnter}
                  onDragOver={handleEditorDragOver}
                  onDragLeave={handleEditorDragLeave}
                  onDrop={handleEditorDrop}
                >
                  {/* Wiki-link highlighting layer (Obsidian-like). Same box and text
                      metrics as the textarea so both wrap identically. */}
                  <div
                    ref={highlightLayerRef}
                    aria-hidden={isEditorFocused ? 'true' : undefined}
                    className={`${NOTEBOOK_EDITOR_TEXT_CLASS} absolute inset-0 z-[1] min-h-[600px] rounded-card border-transparent bg-white text-[#111827] opacity-100 transition-opacity duration-150 [scrollbar-color:transparent_transparent] dark:bg-[#1F2937] dark:text-[#F9FAFB] ${
                      isEditorFocused ? 'pointer-events-none' : 'pointer-events-auto'
                    }`}
                    onClick={() => editorRef.current?.focus()}
                    onScroll={(e) => {
                      const textarea = editorRef.current;
                      if (textarea && textarea.scrollTop !== e.currentTarget.scrollTop) {
                        textarea.scrollTop = e.currentTarget.scrollTop;
                      }
                    }}
                  >
                    <div>
                      {(() => {
                        const text = page.content || "";
                        if (isPlaceholderPage(page)) {
                          return (
                            <span className="text-[#9CA3AF]">
                              {"This page hasn't loaded yet, so editing is paused to keep your notes safe.\nCheck your connection; it will open here as soon as it loads."}
                            </span>
                          );
                        }
                        if (!text) {
                          return (
                            <span className="text-[#9CA3AF]">
                              {"Start typing your notes...\n- Use [[Page Name]] to link to other pages\n- Bullet points supported with '-' or '*'"}
                            </span>
                          );
                        }
                        return renderNotebookPreviewText(
                          text,
                          sections,
                          navigateToNotebookPage,
                          removeLinkedObservation
                        );
                      })()}
                    </div>
                  </div>
                    <textarea
                      ref={editorRef}
                      id="notebook-wiki-editor"
                      data-notebook-wiki-editor="true"
                      className={`${NOTEBOOK_EDITOR_TEXT_CLASS} relative z-[2] block h-full min-h-[600px] w-full resize-none rounded-card border-[#E5E7EB] bg-transparent text-transparent transition-[box-shadow,border-color] duration-300 focus:outline-none focus:ring-2 focus:ring-[#3B82F6] dark:border-[#374151] ${
                        isEditorFocused ? 'pointer-events-auto' : 'pointer-events-none'
                      }`}
                      style={{
                        caretColor:
                          typeof document !== "undefined" &&
                          document.documentElement.classList.contains("dark")
                            ? "#F9FAFB"
                            : "#111827",
                      }}
                      value={encodeNotebookEditorText(page.content)}
                      readOnly={isPlaceholderPage(page)}
                      aria-readonly={isPlaceholderPage(page) || undefined}
                      onChange={handleEditorInput}
                      onFocus={() => setIsEditorFocused(true)}
                      onBlur={() => setIsEditorFocused(false)}
                      onScroll={(e) => {
                        const el = e.currentTarget;
                        const layer = highlightLayerRef.current;
                        if (layer && layer.scrollTop !== el.scrollTop) {
                          layer.scrollTop = el.scrollTop;
                        }
                      }}
                      onPaste={handleEditorPaste}
                      onMouseUp={handleMouseUp}
                      onKeyUp={handleMouseUp}
                      onClick={(e) => { e.stopPropagation(); handleTextClick(e); }}
                      onKeyDown={(e) => {
                        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
                          e.preventDefault();
                          handleUndo();
                        }
                      }}
                      placeholder=""
                      spellCheck={false}
                    />

                    {isImageDragOver && (
                      <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0 z-[3] min-h-[600px] rounded-card bg-[#3B82F6]/[0.04] ring-2 ring-inset ring-[#3B82F6]/70"
                      >
                        <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded bg-white/90 px-2 py-0.5 text-xs text-[#374151] shadow-sm dark:bg-[#111827]/90 dark:text-[#D1D5DB]">
                          Drop to add as a sticky image
                        </span>
                      </div>
                    )}
                    {uploadingImage && !selectionPosition && (
                      <span
                        role="status"
                        className="pointer-events-none absolute bottom-3 right-3 z-[3] rounded bg-white/90 px-2 py-0.5 text-xs text-[#6B7280] shadow-sm dark:bg-[#111827]/90 dark:text-[#9CA3AF]"
                      >
                        Uploading image…
                      </span>
                    )}

                    {/* Autocomplete Dropdown */}
                    {autocompleteOpen && (
                      <WikiLinkAutocomplete
                        isOpen={autocompleteOpen}
                        query={autocompleteQuery}
                        sections={sections}
                        onSelect={handleAutocompleteSelect}
                        onClose={() => {
                          setAutocompleteOpen(false);
                          setAutocompleteQuery('');
                          setLinkStartIndex(-1);
                        }}
                        position={autocompletePosition}
                      />
                    )}
                    
                    {/* Selection action buttons - shown only when text is selected */}
                    {selectionPosition && (
                      <SelectionToolbar
                        anchor={selectionPosition.anchor}
                        uploading={uploadingImage}
                        editorRef={editorRef}
                        onFormat={(marker) => runSelectionToolbarAction(() => applyInlineFormat(marker))}
                        onClear={() => runSelectionToolbarAction(clearInlineFormatting)}
                        onLink={openObservationLinkPicker}
                        onAddImage={() => runSelectionToolbarAction(triggerImageUpload)}
                        onDismiss={dismissSelectionToolbar}
                      />
                    )}
                  </div>
              </div>
              {backlinks.length > 0 && (
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-1 text-xs leading-5 text-[#6B7280] dark:text-[#9CA3AF]" onClick={(e) => e.stopPropagation()}>
                  <span className="font-medium">Linked from</span>
                  {backlinks.map((link) => (
                    <button
                      key={link.pageId}
                      type="button"
                      className="max-w-[16rem] truncate text-[#374151] underline-offset-2 hover:text-[#2563EB] hover:underline dark:text-[#D1D5DB] dark:hover:text-[#60A5FA]"
                      onClick={() => navigateToNotebookPage(link)}
                      title={link.sectionName ? `${link.sectionName} › ${link.pageName}` : link.pageName}
                    >
                      {link.pageName}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Sticky Notes Grid */}
            <StickyImagesPanel
              notes={page.stickyImages}
              collapsed={stickyNotesCollapsed}
              onToggleCollapsed={() => setStickyNotesCollapsed(!stickyNotesCollapsed)}
              highlightedId={highlightedImageId}
              onOpen={openGallery}
              onDelete={(note) => confirmDeleteStickyNote(note)}
            />
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center px-6">
            <div className="theme-panel-alt theme-border flex max-w-md flex-col items-center rounded-2xl border px-6 py-8 text-center shadow-subtle">
              <div className="theme-accent-soft mb-4 flex h-12 w-12 items-center justify-center rounded-2xl">
                <BookOpenIcon className="h-6 w-6 text-[#3B82F6] dark:text-[#60A5FA]" />
              </div>
              <h2 className="text-lg font-semibold text-[#111827] dark:text-[#F9FAFB]">{hasAnyPage ? 'Pick a page to start writing' : 'Start your trading notebook'}</h2>
              <p className="mt-2 text-sm leading-6 text-[#6B7280] dark:text-[#9CA3AF]">{hasAnyPage ? 'Choose a page from the list, or create a new one.' : 'Create a page for trade reviews, strategy notes, or ideas you want to connect later.'}</p>
              <button
                type="button"
                onClick={createNotebookPage}
                className="mt-5 inline-flex items-center gap-2 rounded-lg bg-[#3B82F6] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#2563EB]"
              >
                <PlusIcon className="h-4 w-4" />
                Create Page
              </button>
            </div>
          </div>
        )}
        
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => {
            const files = e.target.files;
            if (files && files.length > 0) {
              handleImageUpload(files);
              e.target.value = ''; // Reset input
            }
          }}
          className="hidden"
        />
        
        {/* Image Modal */}
        {showImageModal && selectedImage && (
          <div 
            className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 animate-fadeIn" 
            onClick={() => setShowImageModal(false)}
          >
            <div className="relative max-w-5xl max-h-5xl p-4 animate-scaleIn">
              <button
                onClick={() => setShowImageModal(false)}
                className="absolute top-2 right-2 w-12 h-12 bg-red-500 hover:bg-red-400 hover:scale-110 rounded-full flex items-center justify-center text-white z-10 transition-all duration-300 shadow-lg hover:shadow-red-500/30"
              >
                <XMarkIcon className="w-7 h-7" />
              </button>
              <img
                src={selectedImage}
                alt="Sticky note"
                className="max-w-full max-h-full object-contain rounded-xl shadow-3xl hover:scale-105 transition-transform duration-300"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        )}
        
        <ZoomableImageModal
          isOpen={showGalleryModal && galleryImages.length > 0}
          images={galleryImages}
          index={currentImageIndex}
          onClose={() => setShowGalleryModal(false)}
          onPrev={() => setCurrentImageIndex(prev => prev > 0 ? prev - 1 : galleryImages.length - 1)}
          onNext={() => setCurrentImageIndex(prev => prev < galleryImages.length - 1 ? prev + 1 : 0)}
          showNavigation={galleryImages.length > 1}
        />
        
      </main>
    </div>
  );
} 
